// deploy-marker: sw-update-flow-test — bump on PR #87 to force a fresh
// radar.[contenthash].js so the deployed test build differs from the
// previous one and the new banner flow can be exercised end-to-end.
import { View } from 'ol';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Vector from 'ol/source/Vector';
import { fromLonLat, transform, transformExtent } from 'ol/proj';
import sync from 'ol-hashed';
import Feature from 'ol/Feature';
import Polygon, { circular } from 'ol/geom/Polygon';
import {
  Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text,
} from 'ol/style';
import LatLon from 'geodesy/latlon-spherical';
import WMSCapabilities from 'ol/format/WMSCapabilities';
import { VERSION as OL_VERSION } from 'ol/util';
import dayjs from 'dayjs';
import 'dayjs/locale/fi';
import utcPlugin from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import durationPlugin from 'dayjs/plugin/duration';
import { FRAME_COUNT, FRAME_STEPS } from './constants';
import Timeline from './timeline';
import createPane from './pane';
import wmsServerConfiguration, { edrLayerInfo } from './config';
import createLongPressHandler, { longPressMenuOpener } from './longpress';
import initPoiMenu, { enforceExclusive } from './ui/poiMenu';
import initTools from './tools';
import initRangeCircle from './rangeCircle';
import initFreehand from './freehand';
import initSectionLine from './sectionLine';
import initCrossSection from './crossSection';
import initProbe from './probe';
import initRadarSite from './radarSite';
import initCrosshair from './crosshair';
import initShare from './share';
import initObsLayer from './obs/obsLayer';
import initLightningLayer from './lightning/lightningLayer';
import { createPlaceNamesLayer, placeNamesStyleLight, placeNamesStyleDark } from './placeNames';
import { createLayerBboxLayer, layerBboxStyleLight, layerBboxStyleDark } from './layerBbox';
import { coverageExtent, coverageOnScreen } from './coverageExtent';
import initStormCells from './stormCells';
import initWindParticles from './particles/windParticles';
import initTrafficMessages from './trafficMessages';
import initWeatherWarnings from './weatherWarnings';
import initWeatherCameras from './weatherCameras';
import initTrains from './trains';
import initGliders from './gliders';
import initTrainLocations from './trainLocations';
import initAirspace from './airspace';
import initAirfields from './airfields';
import initRescueVessels from './ais/rescueVessels';
import initMetar from './metar/metarLayer';
import initTelemetryPanel from './ui/telemetryPanel';
import railwayStationsUrl from './data/railway-stations-finland.geojson';
import radarSitesFallbackUrl from './data/radars-finland.geojson';
import initOwnLocation from './ownLocation';
import initOwnLocationMenu from './ui/ownLocationMenu';
import initPlaceSearch from './ui/placeSearch';
import initSpeedDial from './ui/speedDial';
import initRadarStrip from './ui/radarStrip';
import initRadarLegend from './ui/radarLegend';
import initServerStatus from './ui/serverStatus';
import createLayerPanel from './ui/layerPanel';
import initSearchHighlight from './search/searchHighlight';
import FramePool from './animation/framePool';
import playbackPhase from './animation/playbackPhase';
import { setGetMapLimits } from './wms/requestShape';
import { canInterpolate } from './animation/interpolation';
import createInterpolationController from './animation/interpolation/poolController';
import {
  NOWCAST_LAYER, OBSERVED_LAYER, NOW_INDEX, FUTURE_STEPS,
  parseReferenceTimes, newestReferenceMs, timeLayerFor, edrLayerFor,
  makeRadarSlotProvider, makeCoverageSlotProvider,
} from './nowcast';
import { track } from './analytics';

dayjs.locale('fi');
dayjs.extend(utcPlugin);
dayjs.extend(localizedFormat);
dayjs.extend(durationPlugin);

const options = {
  defaultRadarLayer: 'fmi-radar-composite-dbz',
  defaultLightningLayer: 'observation:lightning',
  defaultObservationLayer: 'observation:airtemperature',
  radialSpacing: 30,
  frameRate: 2, // fps
  defaultFrameRate: 2, // fps
  imageRatio: 1.5,
  wmsServerConfiguration,
};

const DEBUG = false;

function safeParseJSON(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key)); return v != null ? v : fallback; } catch (e) { return fallback; }
}

const metPosition = safeParseJSON('metPosition', []);
const metZoom = Number(localStorage.getItem('metZoom')) || 9;
let ownPosition = [];
let ownPosition4326 = [];
let ownLocation;
let ownLocationMenu;
// Assigned once the POI registry below exists; same forward-declaration pattern
// as ownLocationMenu, so the menu openers above can refresh it.
let poiMenu;
let speedDial = null;
let radarStrip = null;
let radarLegend = null;
let serverStatus = null;
let placeSearch = null;
let tools = null;
let rangeCircle = null;
let freehand = null;
let sectionLine = null;
let crossSection = null;
// Drag-to-draw tools own the whole pointer sequence while armed. A tap that
// aborts a stroke (below Draw's clickTolerance) still emits a map click, so
// the click routers bail out for these tools instead of opening popups.
const DRAW_TOOL_NAMES = new Set(['rengas', 'piirto', 'poikkileikkaus']);
let probe = null;
let radarSite = null;
let startDate = new Date(Math.floor(Date.now() / 300000) * 300000 - 300000 * FRAME_STEPS);
// Handle of the currently-running playback loop (now a requestAnimationFrame
// id — was a setInterval handle before the RAF refactor). Null when paused.
let animationId = null;
let lastAdvance = 0;
let lastWarpTick = 0;
// True while the user is mid pan/zoom on any pane — gates clock advance and
// warp renders. Declared up here (used by the pane clock-gating helpers and
// renderTick) so it precedes its first reference.
let isInteracting = false;
const layerInfo = {};
let timeline;
let mapTime = '';
let appFullscreen = false;
const framePools = {
  satelliteLayer: null,
  radarLayer: null,
  lightningWmsLayer: null,
};
// All created panes (index 0..3), cached across layout switches. Declared early
// so the interpolator/timeline helpers above the pane bootstrap can reference
// it. pane 0 is pushed in the bootstrap below; setLayout() adds the rest.
const panes = [];

// The four content categories + their pill icon and shared sublayer-menu id.
// Declared up here so the per-pane init helpers (initNewPane / buildPanePill)
// can reference them.
const PILL_CATEGORIES = ['satelliteLayer', 'radarLayer', 'lightningLayer', 'observationLayer'];
const CATEGORY_UI = {
  satelliteLayer: { icon: 'satellite_alt', menu: 'satelliteLongPressMenu', aria: 'Satelliitti' },
  radarLayer: { icon: 'radar', menu: 'radarLongPressMenu', aria: 'Säätutka' },
  lightningLayer: { icon: 'bolt', menu: 'lightningLongPressMenu', aria: 'Salamat' },
  observationLayer: { icon: 'thermostat', menu: 'observationLongPressMenu', aria: 'Havainto' },
};

// Three interpolation modes:
//   - 'off'       : no warp layer; animation shows discrete frames only.
//   - 'crossfade' : warp layer with zero-flow — smooth A→B fade.
//   - 'flow'      : warp layer with LK-computed optical flow — motion-
//                   compensated interpolation.
// Resolved on boot from URL override (?interp=…) then localStorage.
// Default is 'off' (discrete frames, the historical behavior). A
// stored mode that's no longer in VALID_INTERP_MODES — e.g. if we
// ever rename or drop an option — is ignored and the default wins,
// so the live storage format is self-healing across releases.
const VALID_INTERP_MODES = ['off', 'crossfade', 'flow'];
const INTERP_MODE_KEY = 'interpMode';
const DEFAULT_INTERP_MODE = 'off';
let interpMode = DEFAULT_INTERP_MODE;
let interpCapable = false;

function readInitialInterpMode() {
  const urlMode = new URLSearchParams(window.location.search).get('interp');
  if (VALID_INTERP_MODES.includes(urlMode)) return urlMode;
  const stored = localStorage.getItem(INTERP_MODE_KEY);
  if (VALID_INTERP_MODES.includes(stored)) return stored;
  return DEFAULT_INTERP_MODE;
}

const {
  attachInterpolators, detachPaneInterpolators, detachInterpolators, setUseFlow,
} = createInterpolationController({
  getMode: () => interpMode,
  getPanes: () => panes,
  getActivePanes: activePanes,
  isPlaying: () => animationId !== null,
  debug,
});

canInterpolate().then((ok) => {
  interpCapable = ok;
  interpMode = ok ? readInitialInterpMode() : DEFAULT_INTERP_MODE;
  debug(`[tutka] INTERP: capable=${ok} mode=${interpMode}`);
  trackBoot({ capable: ok, mode: interpMode });
  attachInterpolators();
  updateInterpChipsState();
}).catch((err) => {
  debug(`[tutka] INTERP probe failed: ${err && err.message}`);
  trackBoot({ capable: false, mode: DEFAULT_INTERP_MODE, error: true });
});

function setInterpMode(mode) {
  if (!VALID_INTERP_MODES.includes(mode)) return;
  if (mode !== 'off' && !interpCapable) return;
  if (interpMode === mode) return;
  const prev = interpMode;
  interpMode = mode;
  localStorage.setItem(INTERP_MODE_KEY, mode);
  if (mode === 'off') {
    detachInterpolators();
  } else if (prev === 'off') {
    attachInterpolators();
  } else {
    // Swap between crossfade and flow on existing interpolators —
    // cheaper than rebuilding the whole pipeline.
    const useFlow = mode === 'flow';
    setUseFlow(useFlow);
  }
  updateInterpChipsState();
  track('interp-mode', { mode });
}

function updateInterpChipsState() {
  document.querySelectorAll('#overflowMenu .chip[data-interp]').forEach((chip) => {
    const m = chip.getAttribute('data-interp');
    chip.setAttribute('aria-checked', String(m === interpMode));
    if (!interpCapable && m !== 'off') {
      chip.setAttribute('aria-disabled', 'true');
    } else {
      chip.removeAttribute('aria-disabled');
    }
  });
}
// Per-(pane, category) timeline state, keyed `${paneIndex}:${name}`. A timeline
// cell is "loaded" only when every visible (pane, layer) pool has that index
// loaded; "flow pending" if any interpolator-bearing pool isn't flow-ready.
// buildPanePools() seeds the keys for each pane as it's created.
//
// poolFlowStates is only populated for pools that have an interpolator attached
// (radar, satellite when interp is enabled). True means both endpoints of the
// pair starting at this index are loaded AND the interpolator has a computed
// flow field — playback through this timestep shows a motion-compensated warp
// instead of a jump to the next discrete frame.
const poolLoadStates = {};
const poolFlowStates = {};

const VISIBLE = new Set(safeParseJSON('VISIBLE', ['radarLayer']));
// Per-layer "is the chosen time inside this layer's data range?" — updated
// by setTime, read by renderTick. Out-of-range layers must skip both
// pool.setWindow/showTime (in setTime) AND pool.showInterpolated (in
// renderTick) — otherwise the FramePool keeps swapping sources for the
// requested old time and the WMS server returns whatever it has closest,
// painting wrong-timestep frames.
const LAYER_IN_RANGE = {};

// Per-category sublayer memory: { radarLayer: 'fmi-radar-composite-dbz', ... }
// Saved on every updateLayer() call, restored when the matching WMS
// GetCapabilities response confirms the stored sublayer is still
// supported. Unknown / dropped layers are evicted automatically.
const ACTIVE_LAYERS = (() => {
  const raw = safeParseJSON('ACTIVE_LAYERS', null);
  return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
})();
function persistActiveLayers() {
  localStorage.setItem('ACTIVE_LAYERS', JSON.stringify(ACTIVE_LAYERS));
}

// Per-category style memory: { radarLayer: 'radar_smhi', ... }. The chosen
// colormap outlives both the session and a product switch, which is why it is
// kept here rather than read back off the source params: every dBZ product on
// meteocore advertises the same five styles, so a style picked on the Finnish
// composite is meant to follow the user to Sweden — and to survive a detour
// through a product that advertises none of them, which resets STYLES on the
// source but must not be taken as the user changing their mind.
//
// Applied wherever the params are written (updateLayer) and, for the products
// no switch ever touches, by restoreActiveStyle once GetCapabilities says which
// styles exist. Only pane 0 persists; other panes seed from it.
const ACTIVE_STYLES = (() => {
  const raw = safeParseJSON('ACTIVE_STYLES', null);
  return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
})();
function persistActiveStyles() {
  localStorage.setItem('ACTIVE_STYLES', JSON.stringify(ACTIVE_STYLES));
}

function updateTimelineCell(i) {
  if (!timeline) return;
  let allLoaded = true;
  let flowPending = false;
  // Aggregate across every active pane's visible pools — the shared timeline
  // reflects the whole split: a cell is ready only when all panes have it.
  for (const pane of activePanes()) {
    for (const name of Object.keys(pane.framePools)) {
      const pool = pane.framePools[name];
      if (!pool || !pane.VISIBLE.has(name)) continue; // eslint-disable-line no-continue
      const key = `${pane.index}:${name}`;
      const load = poolLoadStates[key];
      if (!load || !load[i]) allLoaded = false;
      // A pool only marks flow-pending if it actually has an interpolator
      // attached. Lightning/observation (no interp) and radar/satellite with
      // mode=off never flag this cell.
      const flow = poolFlowStates[key];
      if (pool.interpolator && (!flow || !flow[i])) flowPending = true;
    }
  }
  timeline.setLoadState(i, allLoaded);
  // Flow-pending only meaningful when the cell is loaded — a not-
  // yet-loaded cell is shown as "loading" with priority anyway.
  timeline.setFlowPending(i, allLoaded && flowPending);
}

function recomputeAllTimelineCells() {
  for (let i = 0; i < FRAME_COUNT; i++) updateTimelineCell(i);
}

// IS_DARK: null = auto (follow OS), true = dark, false = light.
//
// Dark is the app's default, not "auto": radar echoes are a bright palette and
// they read against a dark basemap the way weather displays have always shown
// them, so a visitor who has never touched the theme chips gets dark even on a
// light-mode OS. Auto is still one tap away — it is now an explicit choice, and
// therefore has to persist as one. It stores the string 'auto', because the
// absence of the key can no longer mean it: absent is dark.
//
// The key predates this and holds a bare boolean for everyone who already
// picked light or dark; those keep working untouched.
const STORED_THEME = safeParseJSON('IS_DARK', true);
let IS_DARK = STORED_THEME === 'auto' ? null : STORED_THEME !== false;
let IS_TRACKING = safeParseJSON('IS_TRACKING', false);
let IS_FOLLOWING = safeParseJSON('IS_FOLLOWING', false);

function debug(str) {
  if (DEBUG) {
    try {
      // eslint-disable-next-line no-console
      console.log(str);
    } catch (e) { /* ignore */ }
  }
}

// OL layer name → headline event name. Only the four content categories
// emit per-category events; helper layers (basemap, position, etc.) are
// silent.
const CATEGORY_EVENT = {
  satelliteLayer: 'satellite',
  radarLayer: 'radar',
  lightningLayer: 'lightning',
  observationLayer: 'observation',
};

function trackCategory(layer, props) {
  const event = CATEGORY_EVENT[layer.get('name')];
  if (event) track(event, props);
}

ImageLayer.prototype.setLayerUrl = function (url) {
  debug(`Set layer url: ${url}`);
  this.getSource().setUrl(url);
};

ImageLayer.prototype.setLayerStyle = function (style, source) {
  debug(`Set layer style: ${style}`);
  this.getSource().updateParams({ STYLES: style });
  // `source` marks a user action (today: the layer panel's style chips), which
  // is the only thing worth remembering — the restore path re-applies a stored
  // style without one and so cannot re-record what it just read.
  if (source) {
    const pane = paneOf(this);
    const category = this.get('name');
    if (category) {
      pane.ACTIVE_STYLES[category] = style;
      if (pane.index === 0) persistActiveStyles();
    }
    trackCategory(this, { action: 'style', style, source });
  }
  // No pane guard: refresh re-reads pane 0's params, so a style change on
  // another pane is just a cache-hit re-render of an unchanged legend.
  if (radarLegend && this.get('name') === 'radarLayer') radarLegend.refresh();
};

// STYLES
const style = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.6)',
  }),
  stroke: new Stroke({
    color: '#D32D25',
    width: 1,
  }),
  text: new Text({
    font: '16px Calibri,sans-serif',
    fill: new Fill({
      color: '#fff',
    }),
    stroke: new Stroke({
      color: '#000',
      width: 2,
    }),
    offsetX: 0,
    offsetY: -20,
  }),
});

const radarStyle = new Style({
  image: new CircleStyle({
    radius: 4,
    fill: null,
    stroke: new Stroke({ color: 'red', width: 2 }),
  }),
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: '#fff',
    }),
    stroke: new Stroke({
      color: '#000',
      width: 3,
    }),
    offsetX: 0,
    offsetY: -15,
  }),
});

// Light + dark label colours swap from setMapLayer. A single colour set
// can't satisfy both: a black halo on the light basemap reads as thick
// and dominates the dim-white fill; a white halo on the dark basemap
// glows around the dim-grey fill. Invert per theme so the halo always
// sinks into the background rather than ringing the text.
const icaoTextColors = {
  light: { fill: '#222', halo: '#ffffff' },
  dark: { fill: '#cccccc', halo: '#000000' },
};
// Airfields (Lentokentät) — a filled disc the size and shape of the railway
// stations' mark. Replaces the hollow purple ring: a
// filled disc holds its colour at marker size, where a 2px ring mostly showed
// whatever was underneath it, and the two POI layers reading as one family is
// the point rather than a side effect.
//
// Ringed in white exactly as the station mark is — same radius, same stroke —
// so the two really are one family, separated only by hue. The white ring is
// what lifts either dot off a busy basemap, and it is fixed rather than
// per-theme because it does that job on both.
//
// The ICAO code is written in that same colour, so the label and the dot it
// belongs to are tied together where several POI layers are on at once and a
// grey code could belong to any of them. It is therefore the one label here
// that does NOT take the shared per-theme fill — only its halo still inverts
// (see applyIcaoTheme), which is what keeps a pale colour legible against both
// basemaps.
const ICAO_COLOR = 'rgb(224, 145, 224)';
const ICAO_RADIUS = 4.5;
const icaoStyle = new Style({
  image: new CircleStyle({
    radius: ICAO_RADIUS,
    fill: new Fill({ color: ICAO_COLOR }),
    stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
    // The layer declutters its labels (see createPaneLayer) and the mark must
    // not take part: a disc that disappears because its neighbour's code got
    // there first would be an aerodrome the map denies exists. Only the text
    // competes for space.
    declutterMode: 'none',
  }),
  text: new Text({
    // A step smaller than the other POI labels: this one is coloured, and
    // colour already carries the emphasis that size would otherwise have to.
    font: '11px Calibri,sans-serif',
    fill: new Fill({ color: ICAO_COLOR }),
    stroke: new Stroke({ color: icaoTextColors.dark.halo, width: 2.5 }),
    offsetX: 0,
    // Two pixels clear of the station mark's -14. The code sits above a disc
    // that is now filled rather than hollow, so there is no longer any gap
    // showing through the mark itself to separate the two — the separation has
    // to come from the offset instead.
    offsetY: -16,
  }),
});

// Gliding turnpoints (Purjelennon käännöspisteet) — an orange up-triangle, a
// different shape from the airfields' purple disc rather than a different colour,
// which is what keeps them apart at marker size. Shares the airfields'
// per-theme label colours (updated together in applyIcaoTheme).
const turnpointStyle = new Style({
  image: new RegularShape({
    points: 3,
    radius: 5,
    angle: 0,
    fill: null,
    stroke: new Stroke({ color: '#e08020', width: 2 }),
  }),
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({ color: icaoTextColors.dark.fill }),
    stroke: new Stroke({ color: icaoTextColors.dark.halo, width: 2.5 }),
    offsetX: 0,
    offsetY: -14,
  }),
});

// Railway main tracks. Per-theme, like the airfield labels: a muted grey-green
// on the dark basemap — the station green went muddy there against grey ground
// and dark water, while plain white pulled ahead of the radar echo — and the
// darker green on the light one, where anything pale vanishes. The green
// station discs tie the pair together on both.
const railwayTrackColors = {
  light: 'rgba(0, 120, 54, 0.75)',
  dark: 'rgb(158, 172, 157)',
};
const RAILWAY_TRACK_WIDTH = 1.5;
const railwayTrackStyle = new Style({
  stroke: new Stroke({ color: railwayTrackColors.dark, width: RAILWAY_TRACK_WIDTH }),
});

// Railway stations (Rautatieasemat) — a green disc with a white outline.
// Deliberately NOT a square: the weather cameras already use a slate-blue
// square, and at marker size the two were near-indistinguishable. The airfields
// now use this same disc shape in purple (without the white ring), so these two
// are deliberately one family told apart by hue; the turnpoints' orange triangle and the traffic layer's blue
// disc stay distinct by shape and colour both. Shares the airfields' per-theme
// label colours (updated together in applyIcaoTheme).
const railwayStyle = new Style({
  image: new CircleStyle({
    radius: 4.5,
    fill: new Fill({ color: 'rgb(0, 180, 81)' }),
    stroke: new Stroke({ color: '#ffffff', width: 1.5 }),
  }),
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({ color: icaoTextColors.dark.fill }),
    stroke: new Stroke({ color: icaoTextColors.dark.halo, width: 2.5 }),
    offsetX: 0,
    offsetY: -14,
  }),
});

// Municipality polygons (Kunnat) — boundary-only stroke. No labels: every
// municipality is a multipolygon (islands, exclaves) so the per-polygon
// interior-point placement scatters the name across the map, often well
// away from the population centre.
//
// Two style variants because a single neutral gray reads poorly at both
// extremes: the previous near-black stroke dominated the light basemap
// and vanished into the dark one. Light theme gets a soft dark gray at
// low alpha so it sits behind the radar without competing; dark theme
// gets a lavender tint that lifts the borders off the near-black map and
// stays legible on small mobile screens.
const municipalityStyleLight = new Style({
  stroke: new Stroke({
    color: [80, 80, 80, 0.45],
    width: 1,
  }),
});
const municipalityStyleDark = new Style({
  stroke: new Stroke({
    color: [130, 110, 220, 0.95],
    width: 1.5,
  }),
});

// Waterways / fairways (Vesiväylät). Stroke weight follows the fairway
// class — VL1 (commercial main route) reads heaviest, minor routes
// tail off. Cyan/teal hue keeps the layer from colliding with the blue
// of the airspace layer when both are on.
function makeVesivaylatStyles(palette) {
  const stroke = (color, width) => new Style({ stroke: new Stroke({ color, width }) });
  return {
    1: stroke(palette.main, 1.8),
    2: stroke(palette.main, 1.4),
    3: stroke(palette.secondary, 1.1),
    default: stroke(palette.secondary, 0.8),
  };
}
const vesivaylatStylesLight = makeVesivaylatStyles({
  main: [0, 110, 160, 0.7],
  secondary: [0, 130, 180, 0.5],
});
const vesivaylatStylesDark = makeVesivaylatStyles({
  main: [100, 220, 240, 0.9],
  secondary: [80, 190, 220, 0.7],
});
let vesivaylatStyleSet = vesivaylatStylesLight;

// Fairway navigation areas (vaylaalueet_uusi) — MultiPolygons of the
// channel footprint, rendered as a subtle dark wash behind the line
// geometry so the user sees "this whole zone is the fairway, not just
// the centreline". Theme-aware alpha: dark basemap needs more opacity
// to register, light basemap needs less so the wash doesn't dominate.
const vesivaylaAreaFills = {
  light: 'rgba(0, 0, 0, 0.13)',
  dark: 'rgba(0, 0, 0, 0.35)',
};
const vesivaylaAreaStyle = new Style({
  fill: new Fill({ color: vesivaylaAreaFills.light }),
});

// Label nimifi along each fairway when zoomed in enough to actually read
// it. Resolution is m/pixel; ≤ 200 m/px lands around zoom 10-11, which is
// roughly "looking at a single bay or harbour". Above that threshold the
// labels would just be a smear of unreadable text across the country.
const VESIVAYLAT_LABEL_MAX_RES = 200;
const vesivaylatLabelColors = {
  light: { fill: '#003e58', halo: '#ffffff' },
  dark: { fill: '#e0f6ff', halo: '#000000' },
};
const vesivaylatLabelStyle = new Style({
  text: new Text({
    font: '11px Calibri,sans-serif',
    placement: 'line',
    overflow: false,
    fill: new Fill({ color: vesivaylatLabelColors.light.fill }),
    stroke: new Stroke({ color: vesivaylatLabelColors.light.halo, width: 2.5 }),
  }),
});

function vesivaylatStyleFn(feature, resolution) {
  const cls = feature.get('vaylaluokkakoodi');
  const base = vesivaylatStyleSet[cls] || vesivaylatStyleSet.default;
  if (resolution > VESIVAYLAT_LABEL_MAX_RES) return base;
  const name = feature.get('nimifi');
  if (!name) return base;
  vesivaylatLabelStyle.getText().setText(name);
  return [base, vesivaylatLabelStyle];
}

const rangeStyle = new Style({
  stroke: new Stroke({
    color: [128, 128, 128, 0.7],
    width: 0.5,
  }),
  text: new Text({
    font: '16px Calibri,sans-serif',
    fill: new Fill({
      color: '#fff',
    }),
    stroke: new Stroke({
      color: '#000',
      width: 2,
    }),
    offsetX: 0,
    offsetY: 0,
    textAlign: 'left',
  }),
});

//
// LAYERS + MAP
//
// The basemaps, content layers, vector overlays and the OpenLayers Map are now
// built per pane by createPane() in src/pane.js — OL layers cannot be shared
// across maps, so each split pane needs its own instances. The single shared
// `radarSiteSource` below feeds every pane's radar-site layer. The pane
// bootstrap (createPane + pane-0 aliases) runs just below the source.

// Radar-site markers track the live radar network rather than a hand-edited
// file: five meteocore OGC API Features collections are fetched and merged —
// Finland (fi-radar-pvol), Estonia (ee-radar-volume), Denmark
// (dk-radar-volume), the Netherlands (radar-nl-volume) and Croatia
// (radar-hr-volume). The newer two put the country in the middle of the id
// rather than in front; nothing parses these ids except the single-variant rule
// below, which only matches the Finnish -pvol- form and correctly declines the
// rest. Each feature's
// `name` is the official station name used for the label. If both live
// fetches fail (offline PWA, meteocore unreachable), fall back to the bundled
// snapshot so markers still render. Default 'all' loading strategy → the
// loader runs once for the world extent.
const RADAR_SITE_COLLECTIONS = [
  'https://meteocore.app.meteo.fi/features/collections/fi-radar-pvol/items?f=application/geo%2Bjson&limit=1000',
  'https://meteocore.app.meteo.fi/features/collections/ee-radar-volume/items?f=application/geo%2Bjson&limit=1000',
  'https://meteocore.app.meteo.fi/features/collections/dk-radar-volume/items?f=application/geo%2Bjson&limit=1000',
  'https://meteocore.app.meteo.fi/features/collections/radar-nl-volume/items?f=application/geo%2Bjson&limit=1000',
  'https://meteocore.app.meteo.fi/features/collections/radar-hr-volume/items?f=application/geo%2Bjson&limit=1000',
];
const RADAR_SITE_FALLBACK_URL = radarSitesFallbackUrl;

const radarSiteSource = new Vector({
  format: new GeoJSON(),
  attributions: 'FMI / Estonian Environment Agency / DMI / KNMI / DHMZ (CC BY 4.0)',
  loader: (extent, resolution, projection, success, failure) => {
    const readInto = (geojson) => {
      const features = radarSiteSource.getFormat()
        .readFeatures(geojson, { featureProjection: projection });
      radarSiteSource.addFeatures(features);
      return features;
    };
    const fetchJson = (url) => fetch(url).then((res) => {
      if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
      return res.json();
    });

    Promise.all(RADAR_SITE_COLLECTIONS.map(fetchJson))
      .then((collections) => success(collections.flatMap(readInto)))
      .catch((err) => {
        debug(`Live radar sites unavailable (${err}); using bundled fallback.`);
        fetchJson(RADAR_SITE_FALLBACK_URL)
          .then((geojson) => success(readInto(geojson)))
          .catch(() => {
            radarSiteSource.removeLoadedExtent(extent);
            failure();
          });
      });
  },
});

//
// PANES — one OpenLayers Map per split pane, all sharing ONE View so they
// pan/zoom in lockstep (the canonical OL "shared view" pattern). Pane 0 is the
// original single map; its layer/state objects are aliased to the module
// globals below so the rest of this file is unchanged. setLayout() adds further
// panes on the same shared view.
//
const sharedView = new View({
  enableRotation: false,
  center: fromLonLat([26, 65]),
  maxZoom: 16,
  zoom: 5,
  // MeteoCore server contract (see CLAUDE.md): GetMap requests must sit on
  // the discrete Web-Mercator zoom ladder — the server's render/tile cache is
  // keyed on it, and every fractional zoom pays a fresh cold render. Snapping
  // the shared View keeps all panes' requests cache-aligned; pinch/wheel
  // gestures stay smooth and settle on an integer level at gesture end.
  constrainResolution: true,
});

// Observations render as a client-side vector layer fed by the EDR API
// (src/obs/obsLayer.js) — the wms-obs GeoServer that served the raster is
// permanently offline and the WMS fallback path (localStorage.OBS_WMS) has
// been removed. The vector layer's source impersonates the WMS param
// surface (getParams/updateParams LAYERS), so menus, persistence, the layer
// panel and share attributions work unchanged.
const obsController = initObsLayer({ defaultProduct: options.defaultObservationLayer });

// FMI lightning is EDR-backed (the wms-obs GeoServer that rendered
// observation:lightning is permanently offline); the EUMETSAT WMS products
// of the same category (li_afa/rdt) render through a per-pane companion
// ImageWMS layer the controller shows/hides on product switches.
// onWmsProduct routes a WMS product pick through the normal updateLayer
// param/url path against the companion — with skips because the controller
// owns companion visibility and the facade layer already carried the
// category's persistence/tracking.
const lightningController = initLightningLayer({
  defaultProduct: options.defaultLightningLayer,
  onWmsProduct: (companionLayer, id) => updateLayer(companionLayer, id, {
    skipPersist: true, skipVisibility: true, skipTracking: true,
  }),
});

// Place-search highlight pulse: created at module scope (like the obs and
// lightning controllers) so its per-pane layer factory can join paneDeps
// before pane 0 is built; the search UI itself is wired later in main().
const searchHighlight = initSearchHighlight();

// Liikennetiedotteet (Fintraffic road traffic announcements): same shape as the
// storm-cell controller — module scope so its per-pane layer factory can join
// paneDeps, fetching gated on the POI toggle, and setTime feeds it the clock.
// Unlike storm cells it fetches the whole active set at once and uses the
// cursor only to filter, so a clock move costs no network.
const trafficMessages = initTrafficMessages();
const weatherWarnings = initWeatherWarnings();

// Kelikamerat (Fintraffic road weather cameras): markers are wall-clock static
// fixed installations, but the image panel follows the clock — setTime routes
// the cursor so the photo on screen is the one the camera held at the displayed
// frame. The station list is fetched only once the POI is switched on.
const weatherCameras = initWeatherCameras({
  container: document.getElementById('cameraPanel'),
});

// Live OGN aircraft. Wall-clock like the AIS marker — the bridge holds current
// positions only, so setTime does not route here; the WebSocket opens with the
// POI toggle and closes with it.
// Shared one-row readout in the time control. Generic by design: the OGN
// aircraft drive it now, and the AIS vessel and own position are meant to use
// the same panel rather than each growing their own.
const telemetry = initTelemetryPanel({
  container: document.getElementById('telemetryPanel'),
});

const gliders = initGliders({ telemetry });

// Storm cells (Soluntunnistus): OGC API Features cell identification + tracking,
// toggled from the POI menu. Module scope like the obs/lightning controllers so
// its per-pane layer factory can join paneDeps; fetching starts only when the
// POI is switched on (applyPoiVisibility), and setTime feeds it the clock.
const stormCells = initStormCells({ telemetry });
// Flowing particles (Tuuli / Sateen liike): model 10 m wind or the radar's
// precipitation-motion field, one shared WebGL context for every pane. The two
// POI rows are exclusive; applyPoiVisibility picks the source and gates the
// fetching and the frame loop. The clock only picks the field's time.
const wind = initWindParticles({
  // The radar motion field is confined to the radars' coverage discs; the
  // module derives them from these site features (coverageDiscs).
  radarSites: () => radarSiteSource.getFeatures(),
});

// Departure board for a tapped railway station. Wall-clock live rather than
// clock-coupled — a board is about what is next, and no radar frame can
// un-depart a train — so setTime does not route to it; it polls while open.
const trains = initTrains({
  container: document.getElementById('trainPanel'),
  // The same bundled snapshot the marker layer draws, reused to turn the
  // destination short codes in the API's rows into station names.
  stationsUrl: railwayStationsUrl,
});

// Live train positions, on the same Rautatiet toggle as the stations and the
// track network — a moving train is what that layer is a map of. Wall-clock like
// the aircraft and the AIS vessel: the feed carries current positions only, so
// setTime does not route here. Reuses the departure board's station-name lookup
// so both expand short codes from one bundled snapshot.
// Airspace (Ilmatilat) from a bundled openAIP snapshot. Static and clock-free:
// the file has no time dimension, so setTime does not route here.
const airspace = initAirspace();

// Aerodromes (Lentokentät) from the bundled eAIP snapshots, one per country.
// Static and clock-free like the airspace; the METAR layer below borrows its
// records to know where to draw its station plots.
const airfields = initAirfields();

// Search-and-rescue vessels from the marine AIS feed. Wall-clock like the
// aircraft and the trains: AIS says where a vessel is now, so setTime does not
// route here.
const rescueVessels = initRescueVessels({ telemetry });

// METAR station plots. Clock-coupled, unlike the other live layers: MET Norway
// returns 24 h of reports per station in the same request, so the plot can show
// what the aerodrome reported at the frame being displayed.
const metar = initMetar({ telemetry, stations: airfields.stations });

const trainLocations = initTrainLocations({
  telemetry,
  stationName: (code) => trains.stationName(code),
});

// Layer-control panel (style / opacity / info) folded into each category's
// long-press menu. Populated on open via createLongPressHandler's onBeforeShow;
// acts on whichever pane's layer opened the menu. Declared here so buildPanePill
// (called during pane creation below) can reference it.
const layerPanel = createLayerPanel();

// Shared dependencies every pane's layers reference — Style objects/functions
// and the radar-site VectorSource. Passed into createPane so src/pane.js stays
// free of app state.
const paneDeps = {
  options,
  radarSiteSource,
  radarStyle,
  icaoStyle,
  turnpointStyle,
  railwayStyle,
  railwayTrackStyle,
  municipalityStyleLight,
  vesivaylatStyleFn,
  vesivaylaAreaStyle,
  rangeStyle,
  createObservationLayer: obsController.createPaneLayer,
  createLightningLayer: lightningController.createPaneLayer,
  createPlaceNamesLayer,
  createStormCellsLayer: stormCells.createPaneLayer,
  createWindLayer: wind.createPaneLayer,
  createTrafficLayer: trafficMessages.createPaneLayer,
  createWeatherWarningsLayer: weatherWarnings.createPaneLayer,
  createWeatherCameraLayer: weatherCameras.createPaneLayer,
  createGliderLayer: gliders.createPaneLayer,
  createLayerBboxLayer,
  createTrainLocationLayer: trainLocations.createPaneLayer,
  createAirspaceLayer: airspace.createPaneLayer,
  createAirfieldLayer: airfields.createPaneLayer,
  createRescueVesselLayer: rescueVessels.createPaneLayer,
  createMetarLayer: metar.createPaneLayer,
  createSearchHighlightLayer: searchHighlight.createPaneLayer,
};

const pane0 = createPane(document.getElementById('map'), sharedView, {
  ...paneDeps,
  index: 0,
  // Pane 0 reuses the existing module-global state objects so the aliases
  // below point at the very same instances the rest of the file mutates.
  visible: VISIBLE,
  activeLayers: ACTIVE_LAYERS,
  activeStyles: ACTIVE_STYLES,
  layerInRange: LAYER_IN_RANGE,
  framePools,
});
panes.push(pane0);
obsController.bindMap(0, pane0.map);
lightningController.bindMap(0, pane0.map, pane0.lightningWmsLayer);

// Pane-0 aliases — keep the original single-map identifiers working unchanged.
// Only the handles still referenced directly by radar.js are aliased; the
// basemaps, municipality and fairway layers are now reached via `pane.*` in the
// theme/POI fan-outs.
const { map } = pane0;
const {
  satelliteLayer,
  radarLayer,
  lightningLayer,
  observationLayer,
} = pane0;

//
// LAYOUT — 1-up (default) / 2-up / 4-up. Panes are created lazily and cached;
// the active count drives which ones the clock + UI fan out to. Inactive panes'
// divs are display:none, so their map size is 0 and their FramePools idle via
// the FramePool getSize/getVisible guards (no special teardown needed).
//
const LAYOUT_ACTIVE_COUNT = { '1-up': 1, '2-up': 2, '4-up': 4 };
let layoutMode = '1-up';
let activeCount = 1;

function activePanes() {
  return panes.slice(0, activeCount);
}

//
// NOWCAST MODE ("Suomi + ennuste") — observed past + forecast future in one
// window. The mechanics live in src/nowcast.js and the FramePool slot-params
// providers; radar.js only detects the mode and anchors the window.
//
// Boundary of the CURRENTLY ROUTED window: the newest observed frame ("now"),
// epoch-ms. Written ONLY by setTime; null whenever no active pane is in the
// mode, which turns every slot-params provider inert.
let nowcastBoundaryMs = null;
// Module-level getter so per-pool provider closures (built in a loop in
// buildPanePools) don't capture the mutable binding directly.
const getNowcastBoundaryMs = () => nowcastBoundaryMs;

// Mode detection reads pane.ACTIVE_LAYERS — never the layer source's params:
// in nowcast mode the mounted slot's LAYERS flips between the observed and
// forecast product per displayed frame (FramePool swaps sources on showTime),
// so source params are not a stable "current product" signal.
function isNowcastPane(pane) {
  return pane.VISIBLE.has('radarLayer')
    && pane.ACTIVE_LAYERS.radarLayer === NOWCAST_LAYER
    && !(pane.radarSite && pane.radarSite.isSingleSiteActive());
}
function anyNowcastPane() {
  return activePanes().some(isNowcastPane);
}
// Canonical current radar product for UI readers (menu highlight, site-exit
// restore target) — stable across the per-frame slot swaps above.
function radarProductOf(pane) {
  if (pane.radarSite && pane.radarSite.isSingleSiteActive()) {
    return pane.layerss.radarLayer.getSource().getParams().LAYERS;
  }
  return pane.ACTIVE_LAYERS.radarLayer
    || pane.layerss.radarLayer.getSource().getParams().LAYERS;
}

// Clock interaction gating, wired on EVERY active pane's map. A drag fires
// pointerdrag on the pane the user touched; the shared view means moveend then
// fires on every pane. Both are idempotent, so binding to all panes is safe and
// keeps `isInteracting` correct no matter which pane is dragged.
function onPanePointerDrag() { isInteracting = true; }
// The shared view's current extent, or null before the first pane has a size.
// Panes share one View, so pane 0 answers for all of them.
function currentViewExtent() {
  const size = pane0.map.getSize();
  if (!size || !size[0] || !size[1]) return null;
  return sharedView.calculateExtent(size);
}

function onPaneMoveEnd() {
  isInteracting = false;
  // Defer the next advance by a full step after any view change (see the
  // original moveend note: avoids racing the moveend-triggered prefetch).
  lastAdvance = window.performance.now();
  const zoom = Math.min(sharedView.getZoom(), 16);
  localStorage.setItem('metZoom', zoom);
  // Airspace ships one snapshot per country and fetches only the ones the map
  // is actually over, so it needs to hear about every move (it ignores the ones
  // that change nothing).
  airspace.setViewExtent(currentViewExtent());
}
function wirePaneClockGating(pane) {
  pane.map.on('pointerdrag', onPanePointerDrag);
  pane.map.on('moveend', onPaneMoveEnd);
}
// Draw-tool strokes fire pointerdrag (arming the gate) but consume the whole
// pointer sequence without moving the view, so the moveend that normally
// clears the gate never comes — without this callback the clock would stay
// stuck until the next pan.
function onDrawStrokeEnd() {
  isInteracting = false;
  lastAdvance = window.performance.now();
}

// Copy pane 0's current display state (sublayer params, info, opacity,
// visibility) onto a freshly-created pane so a new split starts as a mirror of
// what's on screen; the user then edits it independently (Stage 3).
function clonePaneDisplay(src, dst) {
  ['satelliteLayer', 'radarLayer', 'lightningLayer', 'observationLayer'].forEach((name) => {
    const s = src.layerss[name];
    const d = dst.layerss[name];
    const ssrc = s.getSource();
    const dsrc = d.getSource();
    if (dsrc.getUrl() !== ssrc.getUrl()) dsrc.setUrl(ssrc.getUrl());
    // Strip the nowcast run pin: the source pane's mounted slot may be a
    // forecast frame whose params carry DIM_REFERENCE_TIME + the forecast
    // LAYERS — the clone's providers re-derive both per slot, and the base
    // source must never send a run pin with the observed product.
    dsrc.updateParams({ ...ssrc.getParams(), DIM_REFERENCE_TIME: undefined });
    d.set('info', s.get('info'));
    // The interpolator's transparent swap holds the actual opacity at 0 while
    // the warp renders; _userOpacity carries the user's chosen value (and is
    // undefined whenever no interpolator is attached). Cloning getOpacity()
    // here would capture the swap state and leave the new pane invisible.
    // Carry the user's chosen opacity so the clone's routeLayer doesn't reset it
    // to 1: the persistent _baseOpacity, else the interpolator's live _userOpacity.
    const srcBase = s.get('_baseOpacity');
    if (srcBase !== undefined) d.set('_baseOpacity', srcBase, true);
    const srcUserOpacity = srcBase !== undefined ? srcBase : s.get('_userOpacity');
    d.setOpacity(srcUserOpacity !== undefined ? srcUserOpacity : s.getOpacity());
    d.setVisible(s.getVisible());
    if (s.getVisible()) dst.VISIBLE.add(name); else dst.VISIBLE.delete(name);
    dst.ACTIVE_LAYERS[name] = src.ACTIVE_LAYERS[name];
    // The params copy above already carries STYLES; this carries the intent
    // behind it, so the clone's next product switch honours the same choice.
    dst.ACTIVE_STYLES[name] = src.ACTIVE_STYLES[name];
    // Single-site drill-in is transient per-pane state we deliberately don't
    // clone: the new pane gets its own radarSite instance with no drill-in
    // record, so cloning the site params would leave it stuck without an exit
    // path. Start the new pane on the source pane's saved composite instead;
    // updateLayer clears the cloned site ELEVATION in its params update.
    if (name === 'radarLayer' && src.radarSite && src.radarSite.isSingleSiteActive()) {
      const composite = src.radarSite.getSavedComposite();
      if (composite) {
        updateLayer(d, composite, { skipVisibility: true, skipTracking: true, skipPersist: true });
      }
    }
  });
}

// One-time wiring for a newly-created pane: build its pools, mirror pane 0's
// content, apply the current theme + POI visibility, attach interpolators, and
// gate the clock off its interactions.
function initNewPane(pane) {
  buildPanePools(pane);
  clonePaneDisplay(pane0, pane);
  // Attach the per-pane visibility listener AFTER mirroring (so the clone
  // doesn't fire a burst of change events before the pane is ready).
  for (const name of PILL_CATEGORIES) {
    pane.layerss[name].on('change:visible', onChangeVisible);
  }
  buildPanePill(pane);
  initPaneRadarSite(pane);
  initPaneCrosshair(pane);
  initPaneTraffic(pane);
  if (rangeCircle) rangeCircle.attachPane(pane.map);
  if (freehand) freehand.attachPane(pane.map);
  if (sectionLine) sectionLine.attachPane(pane.map);
  // Radar-site and traffic-announcement taps work in every pane; the other
  // pane-0 click concerns (measure/probe tools, station feature info)
  // deliberately stay pane-0-only.
  pane.map.on('click', (evt) => {
    if (tools && DRAW_TOOL_NAMES.has(tools.getActiveTool())) return;
    const hit = pane.radarSite.findSiteAtPixel(evt.pixel);
    if (hit) {
      pane.radarSite.openCardForFeature(hit);
      return;
    }
    const cameraHit = pane.cameras.findAtPixel(evt.pixel);
    if (cameraHit) {
      pane.cameras.open(cameraHit);
      return;
    }
    // Checked before the station under it: a train standing at a platform sits
    // almost on top of the station marker, and a tap that lands on the thing
    // that is moving is the deliberate one.
    const metarHit = pane.metar.findAtPixel(evt.pixel);
    if (metarHit) {
      pane.metar.open(metarHit);
      return;
    }
    const vesselHit = pane.rescueVessels.findAtPixel(evt.pixel);
    if (vesselHit) {
      pane.rescueVessels.open(vesselHit);
      return;
    }
    const trainHit = pane.trainLocations.findAtPixel(evt.pixel);
    if (trainHit) {
      pane.trainLocations.open(trainHit);
      return;
    }
    const stationHit = pane.trains.findAtPixel(evt.pixel);
    if (stationHit) {
      pane.trains.open(stationHit);
      return;
    }
    if (pane.ownTelemetry && pane.ownTelemetry.findAtPixel(evt.pixel)) {
      pane.ownTelemetry.open();
      return;
    }
    const planeHit = pane.gliders.findAtPixel(evt.pixel);
    if (planeHit) {
      pane.gliders.open(planeHit);
      return;
    }
    const trafficHit = pane.traffic.findAtPixel(evt.pixel);
    if (trafficHit) {
      pane.traffic.openFor(trafficHit);
      return;
    }
    // Cells last: a cell's tap target is its whole footprint, which is far
    // bigger than any marker and would otherwise swallow taps meant for the
    // aerodrome or the train underneath the storm.
    const cellHit = pane.stormCells.findAtPixel(evt.pixel);
    if (cellHit) { pane.stormCells.open(cellHit); return; }
    pane.weatherWarnings.handleClick(evt.pixel);
  });
  setMapLayer(getEffectiveTheme());
  applyPoiVisibility();
  attachInterpolators();
  wirePaneClockGating(pane);
  // Mirror the own-position marker into the new pane if tracking is on.
  if (IS_TRACKING) ownLocation.adoptPane(pane);
}

// Create any panes up to `count` that don't exist yet (targets #map-1..#map-3),
// all sharing the single View.
function ensurePanes(count) {
  for (let i = panes.length; i < count; i++) {
    const el = document.getElementById(`map-${i}`);
    const pane = createPane(el, sharedView, {
      ...paneDeps,
      index: i,
      visible: new Set(pane0.VISIBLE),
      activeLayers: { ...pane0.ACTIVE_LAYERS },
      activeStyles: { ...pane0.ACTIVE_STYLES },
      layerInRange: {},
    });
    panes.push(pane);
    obsController.bindMap(pane.index, pane.map);
    lightningController.bindMap(pane.index, pane.map, pane.lightningWmsLayer);
    initNewPane(pane);
  }
}

function updateLayoutChipsState() {
  document.querySelectorAll('#overflowMenu .chip[data-layout]').forEach((chip) => {
    chip.setAttribute('aria-checked', String(chip.getAttribute('data-layout') === layoutMode));
  });
}

function resizeAllPanes() {
  for (const pane of panes) pane.updateSize();
}

function setLayout(mode) {
  if (!(mode in LAYOUT_ACTIVE_COUNT)) return;
  layoutMode = mode;
  activeCount = LAYOUT_ACTIVE_COUNT[mode];
  ensurePanes(activeCount);
  // Release interpolator GL resources on panes that just dropped out of the
  // active set (e.g. 4-up → 2-up). They re-attach via attachInterpolators()
  // below when they come back. Without this, up to 8 idle GL interpolators
  // would linger in 4-up after shrinking the layout.
  for (let i = activeCount; i < panes.length; i++) detachPaneInterpolators(panes[i]);
  document.body.classList.toggle('split-2', mode === '2-up');
  document.body.classList.toggle('split-4', mode === '4-up');
  // Let the grid reflow before measuring; then size every map (inactive panes
  // collapse to 0 and idle) and re-drive the active panes for the current time.
  window.requestAnimationFrame(() => {
    resizeAllPanes();
    attachInterpolators();
    setTime('keep');
    // After setTime, so newly-active panes' crosshairs receive the current
    // window first (while still hidden) and show() then fetches fresh data
    // once; also hides crosshairs of panes that just dropped out.
    syncCrosshairVisibility();
    recomputeAllTimelineCells();
  });
  updateLayoutChipsState();
  track('layout', { mode });
}

function rangeRings(layer, coordinates, range) {
  if (typeof range === 'number' && layer && coordinates) {
    const ring = circular(coordinates, range);
    const transformedRing = ring.transform('EPSG:4326', map.getView().getProjection());
    const feature = new Feature({
      name: `${range / 1000} km`,
      geometry: transformedRing,
    });

    layer.getSource().addFeatures([feature]);
  }
}

function bearingLine(layer, coordinates, range, direction) {
  const c = new LatLon(coordinates[1], coordinates[0]);
  const p1 = c.destinationPoint(50000, direction);
  const p2 = c.destinationPoint(range * 1000, direction);
  const line = new Polygon([[[p1.lon, p1.lat], [p2.lon, p2.lat]]]);
  layer.getSource().addFeatures([
    new Feature({ name: `${direction}-bearing`, geometry: line.transform('EPSG:4326', map.getView().getProjection()) }),
  ]);
}

// WMS
const currentMapTimeDiv = document.getElementById('currentMapTime');
const currentMapDateDiv = document.getElementById('currentMapDate');
const currentMapKindDiv = document.getElementById('currentMapKind');
// showDate is a window-level decision (setTime): only shown when the animation
// window includes a day other than today, and then for every frame in it.
// nowcastKind is 'obs' | 'forecast' in nowcast mode (the tag shows on EVERY
// frame — Havainto/Ennuste — so the bar's height never changes during
// playback) and null outside it (tag hidden).
function updateMapTimeDisplay(time, showDate, nowcastKind = null) {
  const t = dayjs(time);
  if (!t.isValid()) return;
  // Apply the date visibility every call — the window can shift (following /
  // capabilities refresh) without the displayed frame time changing.
  currentMapDateDiv.classList.toggle('date-hidden', !showDate);
  // Same reasoning: a mode flip can change the kind without the frame time
  // changing, so this can't sit behind the mapTime !== time guard.
  currentMapKindDiv.hidden = !nowcastKind;
  if (nowcastKind) {
    currentMapKindDiv.textContent = nowcastKind === 'forecast' ? 'Ennuste' : 'Havainto';
    currentMapKindDiv.classList.toggle('forecast', nowcastKind === 'forecast');
  }
  if (mapTime !== time) {
    currentMapDateDiv.textContent = t.format('l');
    currentMapTimeDiv.textContent = t.format('LT');
    mapTime = time;
    // Flag the play-bar time when the displayed frame is far behind real-clock
    // now — typically means an upstream feed is lagging (e.g. satellite frozen
    // for days) or the user has scrubbed deliberately into the past.
    const ageHours = dayjs().diff(t, 'hour');
    const stale = ageHours >= 24;
    currentMapTimeDiv.classList.toggle('stale', stale);
    currentMapDateDiv.classList.toggle('stale', stale);
    // In fullscreen the bottom-left chip mirrors this data time — refresh it
    // now so it tracks each frame without waiting for the 1 s clock tick.
    if (appFullscreen) renderTimeChip();
  }
}

// First/last frame times printed under the scrub strip.
const timelineStartEl = document.getElementById('timelineStart');
const timelineEndEl = document.getElementById('timelineEnd');

function updateCanonicalPage() {
  let page = '';
  if (satelliteLayer.getVisible()) {
    const split = satelliteLayer.getSource().getParams().LAYERS.split(':');
    page = `${page}/${(split.length > 1) ? split[1] : split[0]}`;
  }
  if (radarLayer.getVisible()) {
    const split = radarLayer.getSource().getParams().LAYERS.split(':');
    page = `${page}/${(split.length > 1) ? split[1] : split[0]}`;
  }
  if (lightningLayer.getVisible()) {
    const split = lightningLayer.getSource().getParams().LAYERS.split(':');
    page = `${page}/${(split.length > 1) ? split[1] : split[0]}`;
  }
  if (observationLayer.getVisible()) {
    const split = observationLayer.getSource().getParams().LAYERS.split(':');
    page = `${page}/${(split.length > 1) ? split[1] : split[0]}`;
  }
  debug(`Set page: ${page}`);
}

function setTime(action = 'next', seekIndex = 0) {
  let resolution = 300000;
  let end = Math.floor(Date.now() / resolution) * resolution - resolution;
  let start = end - resolution * FRAME_STEPS;

  // Compute the shared 13-frame window from the UNION of every active pane's
  // visible layers — time is identical across panes, so one window drives them
  // all. The newest-frame cap (end) and the resolution come from whichever
  // visible layer in any pane is freshest / coarsest.
  for (const pane of activePanes()) {
    for (const item of pane.VISIBLE) {
      const wmslayer = pane.layerss[item].getSource().getParams().LAYERS;
      // getLayerInfo only sets `.time` when GetCapabilities advertises a
      // Dimension, and a malformed dimension can yield non-numeric fields.
      // A layer without a valid time can't constrain the window — skip it
      // rather than throwing here on every tick (which freezes playback).
      // timeLayerFor maps the nowcast entry onto the observed composite:
      // the window anchors on observations (the forecast product's time.end
      // is +2 h out and must never move "now"), and the mapping is stable
      // across nowcast mode's per-frame LAYERS flips.
      const effLayer = timeLayerFor(wmslayer);
      const t = effLayer in layerInfo ? layerInfo[effLayer].time : null;
      // The EDR vector layers (observations, FMI lightning) snap raw events
      // onto whatever window the raster layers pick — they must not coarsen
      // the shared resolution nor cap the newest frame. Their seeded
      // layerInfo entries carry no `time` on purpose (see config.js
      // edrLayerInfo), so they fall out via the null check; the explicit
      // obs guard stays as a belt against a future entry gaining `time`.
      const constrains = item !== 'observationLayer';
      if (constrains && t && Number.isFinite(t.end) && Number.isFinite(t.resolution)) {
        if (item === 'radarLayer' || item === 'satelliteLayer' || item === 'observationLayer') {
          end = Math.min(end, Math.floor(t.end / resolution) * resolution);
        }
        resolution = Math.max(resolution, t.resolution);
      }
    }
  }

  end = Math.floor(end / resolution) * resolution;
  // Nowcast mode (any active pane): "now" is the newest observed frame; the
  // window extends FUTURE_STEPS steps past it so cells NOW_INDEX+1..12 are
  // forecast. The boundary is published BEFORE the pools are routed below —
  // the slot-params providers read it live — and nulled when the mode is
  // off, which turns the providers inert.
  const nowcastActive = anyNowcastPane();
  const nowMs = end;
  nowcastBoundaryMs = nowcastActive ? nowMs : null;
  if (nowcastActive) end = nowMs + resolution * FUTURE_STEPS;
  start = end - resolution * FRAME_STEPS;
  // Following anchors on "now" in nowcast mode: a stopped following view
  // shows current weather (the boundary frame), not the +30 min frame, and
  // the 60 s capabilities refresh keeps it pinned there.
  const followAnchorMs = nowcastActive ? nowMs : end;

  switch (action) {
    case 'first':
      startDate = new Date(start);
      break;
    case 'last':
      startDate = new Date(followAnchorMs);
      break;
    case 'seek': {
      // Jump to an explicit frame index (0..12) within the current window.
      const i = Math.max(0, Math.min(12, Math.round(seekIndex)));
      startDate = new Date(start + i * resolution);
      break;
    }
    case 'previous':
      startDate.setMinutes(Math.floor(startDate.getMinutes() / (resolution / 60000)) * (resolution / 60000) - resolution / 60000);
      break;
    case 'keep':
      // No-op on startDate; the clamp below will pull it into [start, end]
      // if a visibility change shrunk the window.
      break;
    case 'next':
    default:
      startDate.setMinutes(Math.floor(startDate.getMinutes() / (resolution / 60000)) * (resolution / 60000) + resolution / 60000);
  }

  if (startDate.getTime() > end) {
    // Wrap the cursor back to the window start. Do NOT recreate the Timeline
    // here: rebuilding wipes every cell's loading/flow class, and nothing
    // re-emits them (the pool state callbacks fire only on change), so the
    // indicators went permanently blank after the first playback loop. A pure
    // wrap doesn't change the window, so the existing cell state stays valid;
    // when the window really shifts, pool.setWindow below triggers per-cell
    // load-state callbacks that repaint through updateTimelineCell.
    startDate = new Date(start);
  } else if (startDate.getTime() < start) {
    startDate = new Date(end);
  }

  if (startDate.getTime() === followAnchorMs && animationId === null) {
    IS_FOLLOWING = true;
    localStorage.setItem('IS_FOLLOWING', JSON.stringify(true));
    debug('MODE: FOLLOW');
    document.getElementById('skipNextButton').classList.add('selectedButton');
  } else {
    IS_FOLLOWING = false;
    localStorage.setItem('IS_FOLLOWING', JSON.stringify(false));
    document.getElementById('skipNextButton').classList.remove('selectedButton');
  }

  // updateTimeLine((startDate.getTime()-start)/resolution);
  timeline.update((startDate.getTime() - start) / resolution);
  timeline.setNowIndex(nowcastActive ? NOW_INDEX : null);
  if (probe) probe.setCursor(startDate.getTime(), start, resolution);
  if (crossSection) crossSection.setCursor(startDate.getTime(), start, resolution);
  // Storm cells show the server's snapshot for the displayed frame — nothing
  // extrapolated, and empty on frames it has none for. The window is passed so
  // the controller knows which frames to prefetch.
  stormCells.setCursor(startDate.getTime(), start, resolution);
  // Wind particles follow the window's newest frame to the nearest model step
  // — a refetch once per step, never a flip mid-loop.
  wind.setCursor(startDate.getTime(), start, resolution);
  // Traffic announcements show only where the displayed frame falls inside the
  // announcement's validity, so scrubbing back hides an incident that had not
  // happened yet at that frame. Filter only — no fetch on a cursor move.
  trafficMessages.setCursor(startDate.getTime(), start, resolution);
  // Kelikamerat: the open camera's photo snaps to the displayed frame, and the
  // window is passed so the surrounding frames' images can be prefetched.
  weatherCameras.setCursor(startDate.getTime(), start, resolution);
  // METAR plots follow the clock too: the 24 h of reports is already in hand, so
  // showing the one current at the displayed frame is a lookup, not a fetch.
  metar.setCursor(startDate.getTime(), start, resolution);
  // Per-pane crosshairs: only active panes get the cursor — inactive panes'
  // reticles are hidden and must not refetch; they pick up the current window
  // on reactivation (setLayout runs setTime before syncCrosshairVisibility).
  for (const pane of activePanes()) {
    if (pane.crosshair) pane.crosshair.setCursor(startDate.getTime(), start, resolution);
  }

  // var startDateFormat = moment(startDate.toISOString()).utc().format()
  // debug("---");
  // debug(startDateFormat);
  // debug(startDate.toISOString());
  const timeISO = startDate.toISOString();
  const tNow = startDate.getTime();
  const timeInterval = `PT${resolution / 60000}M/${timeISO}`;
  const windowInstant = [];
  const windowInterval = [];
  for (let i = 0; i <= FRAME_STEPS; i++) {
    const t = new Date(start + i * resolution).toISOString();
    windowInstant.push(t);
    windowInterval.push(`PT${resolution / 60000}M/${t}`);
  }
  // Two concerns handled here:
  //   1. Mark the layer that has *stale upstream data* (newest frame older
  //      than 24h). The cause of "everything looks weird" is this layer,
  //      not whichever other layer happens to lose its time-overlap.
  //   2. When the chosen time falls outside this layer's data range, hide
  //      its image (opacity 0) so a previously-loaded frame doesn't stay
  //      stuck on screen.
  const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
  // Route one (pane, category) for the current window. Per-pane concerns —
  // opacity, LAYER_IN_RANGE and the pool's window/time. The shared top toolbar's
  // stale-data button reflects pane 0 only; per-pane pills own their own state.
  // `uiName` maps a companion layer (the lightning category's WMS raster,
  // which lives outside layerss) onto the category whose visibility/button
  // state governs it; for layerss entries it equals `name`.
  const routeLayer = (pane, name, window, currentTime, uiName = name) => {
    const olLayer = pane.layerss[name] || pane[name];
    const button = pane.index === 0 ? document.getElementById(`${uiName}Button`) : null;
    const pillBtn = pane.pillButtons && pane.pillButtons[uiName];

    if (!pane.VISIBLE.has(uiName)) {
      if (button) button.classList.remove('stale-data');
      if (pillBtn) pillBtn.classList.remove('stale-data');
      olLayer.setOpacity(1);
      pane.LAYER_IN_RANGE[name] = true;
      return;
    }

    // In nowcast mode the pane's radar range is anchored on the observed
    // composite (the mounted slot's LAYERS flips per frame — not a stable
    // identity) and its end is extended by the forecast product's advertised
    // range so the future cells aren't blanked by the opacity gate below.
    const nowcastHere = name === 'radarLayer' && isNowcastPane(pane);
    const wmslayer = nowcastHere ? OBSERVED_LAYER : olLayer.getSource().getParams().LAYERS;
    const info = wmslayer in layerInfo ? layerInfo[wmslayer].time : null;

    const isStale = !!(info && info.end && Date.now() - info.end > STALE_THRESHOLD_MS);
    if (button) button.classList.toggle('stale-data', isStale);
    if (pillBtn) pillBtn.classList.toggle('stale-data', isStale);

    const ncTime = nowcastHere && NOWCAST_LAYER in layerInfo
      ? layerInfo[NOWCAST_LAYER].time : null;
    const rangeEnd = info && ncTime && Number.isFinite(ncTime.end)
      ? Math.max(info.end, ncTime.end)
      : info && info.end;
    // The EDR obs vector layer handles per-station data gaps itself (a
    // frame with no fresh-enough report simply drops that label) — hiding
    // the whole layer on the WMS capabilities range would blank it on the
    // newest frames whenever the caps timestamp lags the radar window.
    const inRange = name === 'observationLayer'
      || !info || !info.start || !rangeEnd
      || (tNow >= info.start && tNow <= rangeEnd);
    // Respect the user's chosen opacity (persistent _baseOpacity) instead of
    // forcing 1 — otherwise every frame swap during playback resets it to 100%.
    // Don't touch it while the interpolator is parking the layer transparent
    // (Hard rule 7); its own change:opacity listener re-applies the user value.
    if (!olLayer.get('_interpHiding')) {
      const base = olLayer.get('_baseOpacity');
      olLayer.setOpacity(inRange ? (base !== undefined ? base : 1) : 0);
    }
    pane.LAYER_IN_RANGE[name] = inRange;

    if (!inRange) return;

    const pool = pane.framePools[name];
    if (!pool) return;
    pool.setWindow(window);
    pool.showTime(currentTime);
  };
  for (const pane of activePanes()) {
    routeLayer(pane, 'satelliteLayer', windowInstant, timeISO);
    routeLayer(pane, 'radarLayer', windowInstant, timeISO);
    routeLayer(pane, 'lightningLayer', windowInterval, timeInterval);
    routeLayer(pane, 'observationLayer', windowInterval, timeInterval);
    // The lightning category's WMS raster (li_afa/rdt) rides its own pool on
    // the companion layer, gated on the category's visibility/button state.
    // In EDR mode the companion is hidden and its pool idles on the
    // FramePool getVisible guard — skip the routing entirely.
    if (pane.lightningWmsLayer && lightningController.isWmsMode(pane.index)) {
      routeLayer(pane, 'lightningWmsLayer', windowInterval, timeInterval, 'lightningLayer');
    }
  }
  // EDR vector layers: one shared controller each replaces the per-pane
  // FramePools. Window changes trigger a (deduped) fetch; cursor moves only
  // restyle client-side.
  if (obsController) obsController.route(start, resolution, tNow);
  lightningController.route(start, resolution, tNow);

  // First/last frame times under the strip, and the clock's date visibility.
  // Show the date only when the window spans a day other than today — either
  // stale data (whole window in the past) or a window straddling midnight; a
  // ≤1 h window has at most two dates, so start + end cover every frame. When
  // shown, it stays on for the whole window (incl. the today-side frames).
  timelineStartEl.textContent = dayjs(start).format('LT');
  timelineEndEl.textContent = dayjs(end).format('LT');
  const nowcastKind = nowcastActive ? (tNow > nowMs ? 'forecast' : 'obs') : null;
  const today = dayjs();
  const showDate = !dayjs(start).isSame(today, 'day') || !dayjs(end).isSame(today, 'day');
  updateMapTimeDisplay(timeISO, showDate, nowcastKind);
}

//
// TIME CONTROLS
//

function anyPoolZooming() {
  for (const pane of activePanes()) {
    for (const name of Object.keys(pane.framePools)) {
      const p = pane.framePools[name];
      if (p && p.isZoomGestureActive && p.isZoomGestureActive()) return true;
    }
  }
  return false;
}

// One clock owns the discrete observations and fractional warp. Advance and
// paint the new pair together; leaving its old warp visible for another RAF
// creates a repeated hesitation at every observation boundary.
const renderTick = function (now) {
  animationId = window.requestAnimationFrame(renderTick);
  // Skip both advance and warp render while the user is mid-drag,
  // while OL is mid-tween (e.g., smooth zoom from a scroll wheel),
  // or while any pool is inside an active wheel-zoom gesture (we
  // hold the pause until 300ms after the last wheel event so slow
  // scrolls don't fire prefetch bursts between tween gaps).
  // Without these gates, every wheel tick keeps playback
  // advancing, and each advance fires pool.showTime →
  // _prefetchAroundCurrent → 3-6 WMS fetches per visible pool.
  if (isInteracting || map.getView().getAnimating() || anyPoolZooming()) return;

  const phase = playbackPhase(now, lastAdvance, 1000 / options.frameRate);
  lastAdvance = phase.startedAt;
  if (phase.advance) setTime();

  // Keep the ~30 Hz render budget between boundaries. A new observation must
  // bypass the throttle so the timeline and rendered pair change together.
  if (!phase.advance && now - lastWarpTick < 33) return;
  lastWarpTick = now;

  for (const pane of activePanes()) {
    for (const name of Object.keys(pane.framePools)) {
      if (!pane.VISIBLE.has(name)) continue; // eslint-disable-line no-continue
      if (pane.LAYER_IN_RANGE[name] === false) continue; // eslint-disable-line no-continue
      const pool = pane.framePools[name];
      if (pool) pool.showInterpolated(phase.t);
    }
  }
};

function setInterpActiveAll(active) {
  for (const pane of activePanes()) {
    for (const name of Object.keys(pane.framePools)) {
      const pool = pane.framePools[name];
      if (pool && pool.interpolator) pool.setInterpActive(active);
    }
  }
}

const play = function () {
  if (animationId === null) {
    debug('PLAY');
    IS_FOLLOWING = false;
    // An explicit play must never be blocked by a stale drag gate (e.g. a
    // gesture that ended without a moveend).
    isInteracting = false;
    lastAdvance = window.performance.now();
    animationId = window.requestAnimationFrame(renderTick);
    document.getElementById('playstopButton').innerHTML = 'pause';
    setInterpActiveAll(true);
  }
};

const stop = function () {
  if (animationId !== null) {
    debug('STOP');
    IS_FOLLOWING = false;
    window.cancelAnimationFrame(animationId);
    animationId = null;
    document.getElementById('playstopButton').innerHTML = 'play_arrow';
    setInterpActiveAll(false);
  }
};

const skipNext = function () {
  debug('NEXT');
  IS_FOLLOWING = false;
  stop();
  setTime('next');
};

const skipPrevious = function () {
  debug('PREVIOUS');
  IS_FOLLOWING = false;
  stop();
  setTime('previous');
};

const playstop = function () {
  IS_FOLLOWING = false;
  if (animationId !== null) {
    stop();
  } else {
    play();
  }
};

// Start Animation

function getEffectiveTheme() {
  if (IS_DARK !== null) return IS_DARK ? 'dark' : 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Theme is global — the shared Style singletons are mutated once, then every
// pane's vector layers are told to re-render with the new colours.
function applyIcaoTheme(theme) {
  const t = icaoTextColors[theme] || icaoTextColors.dark;
  // Airfield codes keep their own colour and only swap halo — the fill is
  // identity here, not emphasis, so it must survive a theme change. The mark
  // needs nothing per theme: it is its own colour through and through.
  icaoStyle.getText().getStroke().setColor(t.halo);
  turnpointStyle.getText().getFill().setColor(t.fill);
  turnpointStyle.getText().getStroke().setColor(t.halo);
  railwayStyle.getText().getFill().setColor(t.fill);
  railwayStyle.getText().getStroke().setColor(t.halo);
  railwayTrackStyle.getStroke().setColor(
    railwayTrackColors[theme] || railwayTrackColors.dark,
  );
  for (const pane of panes) {
    pane.icaoLayer.changed();
    if (pane.turnpointLayer) pane.turnpointLayer.changed();
    if (pane.railwayLayer) pane.railwayLayer.changed();
    if (pane.railwayTrackLayer) pane.railwayTrackLayer.changed();
  }
}

function applyVesivaylatTheme(theme) {
  vesivaylatStyleSet = theme === 'light' ? vesivaylatStylesLight : vesivaylatStylesDark;
  const t = vesivaylatLabelColors[theme] || vesivaylatLabelColors.dark;
  vesivaylatLabelStyle.getText().getFill().setColor(t.fill);
  vesivaylatLabelStyle.getText().getStroke().setColor(t.halo);
  vesivaylaAreaStyle.getFill().setColor(vesivaylaAreaFills[theme] || vesivaylaAreaFills.light);
  for (const pane of panes) {
    pane.vesivaylatLayer.changed();
    pane.vesivaylaAreaLayer.changed();
  }
}

function setMapLayer(maplayer) {
  debug(`Set ${maplayer} map.`);
  if (maplayer !== 'light' && maplayer !== 'dark') return;
  const light = maplayer === 'light';
  for (const pane of panes) {
    pane.darkGrayBaseLayer.setVisible(!light);
    pane.lightGrayBaseLayer.setVisible(light);
    pane.placeNamesLayer.setStyle(light ? placeNamesStyleLight : placeNamesStyleDark);
    pane.municipalityLayer.setStyle(light ? municipalityStyleLight : municipalityStyleDark);
    pane.stormCellsLayer.setStyle(light ? stormCells.styleLight : stormCells.styleDark);
    pane.trafficLayer.setStyle(light ? trafficMessages.styleLight : trafficMessages.styleDark);
    pane.weatherWarningsLayer.setStyle(light ? weatherWarnings.styleLight : weatherWarnings.styleDark);
    pane.weatherCameraLayer.setStyle(light ? weatherCameras.styleLight : weatherCameras.styleDark);
    pane.gliderLayer.setStyle(light ? gliders.styleLight : gliders.styleDark);
    pane.layerBboxLayer.setStyle(light ? layerBboxStyleLight : layerBboxStyleDark);
    pane.trainLocationLayer.setStyle(
      light ? trainLocations.styleLight : trainLocations.styleDark,
    );
    const airspaceTheme = light ? 'light' : 'dark';
    pane.airspaceControlledLayer.setStyle(airspace.styleFor('controlled', airspaceTheme));
    pane.airspaceRestrictedLayer.setStyle(airspace.styleFor('restricted', airspaceTheme));
    pane.airspaceReservedLayer.setStyle(airspace.styleFor('reserved', airspaceTheme));
    pane.metarLayer.setStyle(light ? metar.styleLight : metar.styleDark);
  }
  wind.setTheme(light ? 'light' : 'dark');
  applyIcaoTheme(maplayer);
  applyVesivaylatTheme(maplayer);
}

function getThemeMode() {
  if (IS_DARK === null) return 'auto';
  return IS_DARK ? 'dark' : 'light';
}

function updateThemeChipsState() {
  const mode = getThemeMode();
  document.querySelectorAll('#overflowMenu .chip[data-theme]').forEach((chip) => {
    const match = chip.getAttribute('data-theme') === mode;
    chip.setAttribute('aria-checked', String(match));
  });
}

function setUserTheme(mode) {
  if (mode === 'auto') {
    IS_DARK = null;
    // Written, not removed: with dark as the default an absent key means dark,
    // so "follow the OS" has to be stored to survive a reload.
    localStorage.setItem('IS_DARK', JSON.stringify('auto'));
    setMapLayer(getEffectiveTheme());
  } else {
    IS_DARK = mode === 'dark';
    localStorage.setItem('IS_DARK', JSON.stringify(IS_DARK));
    setMapLayer(mode);
  }
  updateThemeChipsState();
  track('theme-change', { pref: mode, shown: getEffectiveTheme() });
}

// Resolve the GetMap wire format for a sublayer. Precedence: an explicit
// per-layer config override (`info.format`) wins; otherwise image/webp
// when the serving WMS advertised it in GetCapabilities (smaller payload,
// also supports transparency); otherwise the category default. Always
// returns a concrete format, so switching FROM a webp-capable server back
// to one that lacks it resets FORMAT instead of leaving a stale webp.
function resolveFormat(layer, info) {
  if (info && info.format) return info.format;
  if (info && info.webp && !layer.get('disableWebp')) return 'image/webp';
  return layer.get('defaultFormat') || 'image/png';
}

// Re-apply the wire format to a category's source for whatever sublayer
// it currently shows. Called after each GetCapabilities response so a
// server that advertises image/webp is adopted even when the sublayer
// itself didn't change (e.g. the boot-default layer, which updateLayer
// never re-runs for). FramePool mirrors the FORMAT change to its slots
// via its primary-source `change` listener.
function applyWireFormat(layer) {
  const wmslayer = layer.getSource().getParams().LAYERS;
  const info = layerInfo[wmslayer];
  // Until this sublayer's own GetCapabilities has populated layerInfo we
  // don't yet know whether its server advertises webp. Leave FORMAT at
  // the constructor default rather than churning it to png now and to
  // webp later — the intermediate updateParams triggers a needless
  // FramePool resync and an extra round of slot re-requests at startup.
  if (!info) return;
  const fmt = resolveFormat(layer, info);
  if (layer.getSource().getParams().FORMAT !== fmt) {
    layer.getSource().updateParams({ FORMAT: fmt });
  }
}

function updateLayer(layer, wmslayer, opts = {}) {
  const {
    skipVisibility = false, skipTracking = false, skipPersist = false, source, elevation,
  } = opts;
  debug(`Activated layer ${wmslayer}`);
  if (!skipTracking && source) {
    trackCategory(layer, { action: 'pick', layer: wmslayer, source });
  }
  debug(layerInfo[wmslayer]);
  const pane = paneOf(layer);
  const isPane0 = pane === pane0;
  const info = layerInfo[wmslayer];
  layer.set('info', info);
  if (info && info.url) {
    layer.setLayerUrl(info.url);
  }
  // Record the pane's canonical product BEFORE touching source params: the
  // params write below fires the FramePool primary-source diff synchronously,
  // and its slot-params providers read pane.ACTIVE_LAYERS (nowcast mode) —
  // they must see the product being applied, not the previous one.
  if (!skipPersist) {
    const category = layer.get('name');
    if (category && pane.ACTIVE_LAYERS[category] !== wmslayer) {
      pane.ACTIVE_LAYERS[category] = wmslayer;
      // Only the primary pane persists to localStorage; background panes are
      // re-seeded by mirroring when a split is (re)entered.
      if (isPane0) persistActiveLayers();
    }
  }
  // Reset style if the new layer doesn't support the currently active style
  const currentStyle = layer.getSource().getParams().STYLES || '';
  const baseUpdate = { LAYERS: wmslayer };
  // ELEVATION is only meaningful for single-site radar products (radarSite
  // passes it via opts). Set it in the SAME params update as LAYERS: this
  // clears a previous drill-in's sweep on every layer switch — otherwise
  // composite GetMap requests keep carrying the stale ELEVATION to the
  // server. ol/uri drops undefined-valued params from the request URL.
  baseUpdate.ELEVATION = elevation != null ? elevation : undefined;
  // Apply the resolved wire format (webp / per-layer override / category
  // default) and transparency, falling back to the layer's category
  // default so a switch FROM a transparent overlay back to a full-disc
  // image resets the params correctly.
  const defaultTransparent = layer.get('defaultTransparent');
  baseUpdate.FORMAT = resolveFormat(layer, info);
  const wantTransparent = info && info.transparent !== undefined
    ? info.transparent
    : defaultTransparent;
  if (wantTransparent !== undefined) {
    baseUpdate.TRANSPARENT = wantTransparent ? 'TRUE' : 'FALSE';
  }
  // Which style the incoming product should wear: the remembered one when this
  // product advertises it, else whatever is on screen if it survives the switch,
  // else the server default. The remembered style wins over the on-screen one so
  // that a product which could not honour it (STYLES was reset to '' on the way
  // through) hands the choice back on the way out.
  const validStyles = info && Array.isArray(info.style) ? info.style.map((s) => s.Name) : null;
  const remembered = pane.ACTIVE_STYLES[layer.get('name')] || '';
  let wantStyle = '';
  if (validStyles) {
    if (remembered && validStyles.includes(remembered)) wantStyle = remembered;
    else if (currentStyle && validStyles.includes(currentStyle)) wantStyle = currentStyle;
  }
  if (wantStyle !== currentStyle) baseUpdate.STYLES = wantStyle;
  layer.getSource().updateParams(baseUpdate);
  if (!skipVisibility) {
    if (layer.getVisible()) {
      if (isPane0) updateCanonicalPage();
    } else {
      layer.setVisible(true);
    }
  }
  if (isPane0 && probe && layer === radarLayer) {
    // Pass the elevation (set in single-site mode) so the EDR probe queries the
    // displayed sweep; composites carry no ELEVATION param (z stays null).
    // edrLayerFor: the nowcast entry probes the observed composite — the EDR
    // API has no forecast collection.
    probe.setActiveLayer(layer.getVisible() ? edrLayerFor(wmslayer) : null, {
      z: layer.getSource().getParams().ELEVATION,
    });
  }
  // The crosshair readout is per-pane: each reticle tracks its own pane's
  // radar product (the pistemittaus probe above stays pane-0-only by scope).
  if (pane.crosshair && layer === pane.layerss.radarLayer) {
    pane.crosshair.setActiveLayer(layer.getVisible() ? edrLayerFor(wmslayer) : null, {
      z: layer.getSource().getParams().ELEVATION,
    });
  }
  if (layer === pane.layerss.radarLayer) refreshLayerBbox(pane, wmslayer);
  // Must stay after the STYLES reset above so the legend reads the post-reset
  // style; covers layer picks, nowcast and single-site transitions alike.
  if (isPane0 && radarLegend && layer === radarLayer) radarLegend.refresh();
}

// Sync a pane's radar coverage outline (src/layerBbox.js) to its currently
// active radar product: cleared while the radar category is hidden or its
// GetCapabilities bbox hasn't loaded yet, otherwise the advertised
// EX_GeographicBoundingBox. `wmslayer` defaults to the product the pane's
// radar layer is actually showing, but callers pass the canonical product
// name explicitly where the mounted LAYERS can be a transient nowcast frame.
function refreshLayerBbox(pane, wmslayer = pane.layerss.radarLayer.getSource().getParams().LAYERS) {
  const info = layerInfo[wmslayer];
  const visible = pane.layerss.radarLayer.getVisible();
  const hasBbox = visible && info && Array.isArray(info.bbox);
  pane.setLayerBbox(hasBbox ? info.bbox : null, hasBbox ? info.title : null);
}

// Pan/zoom the map to a layer's advertised coverage. Used by the radar and
// satellite long-press menus so picking e.g. "Ruotsi" recentres on Sweden's
// radar footprint, or "GOES-West" on the eastern Pacific. No-op until the
// layer's GetCapabilities has populated a geographic bounding box, or if the
// transform yields a degenerate extent (e.g. a full-disc box clipped at the
// Web Mercator poles). Antimeridian-straddling boxes: see coverageExtent.js.
function fitToLayerExtent(wmslayer) {
  const info = layerInfo[wmslayer];
  if (!info || !Array.isArray(info.bbox)) return;
  const view = map.getView();
  const extent = coverageExtent(info.bbox, view.getProjection());
  if (!extent) return;
  // Only when the pick would otherwise leave the user looking at nothing.
  //
  // The recentre exists for the case where it is the only sensible move: you
  // are over Finland, you pick Puola, and without it the map shows empty
  // country while the data is 1500 km away. But it was firing on every pick,
  // including the ones where the new product already covers what is on screen
  // — switching between the Finnish composite and the European one over
  // Helsinki threw the view out to the whole of Europe and made the user pan
  // back. Any overlap at all means the answer is on screen already, so the
  // view is left exactly where the user put it.
  const view3857 = currentViewExtent();
  if (view3857 && coverageOnScreen(view3857, extent, view.getProjection())) return;
  view.fit(extent, {
    size: map.getSize(),
    // Leave room for the top toolbar and bottom timeline so the footprint
    // isn't tucked under the chrome.
    padding: [60, 30, 90, 30],
    // Guard against zooming to street level on a tiny footprint; country-
    // and continent-scale boxes land well below this and are unaffected.
    maxZoom: 12,
    duration: 500,
  });
}

// Per-layer override map: any wms entry that declares a specific `layer`
// (e.g. de/fi/eu/no/se/dk on the meteocore endpoint) provides the
// attribution/license/title fallback for THAT layer specifically. Lets a
// single GetCapabilities fetch serve a multi-source group correctly.
// (Defined up here — before its getLayers/getLayerInfo consumers — because
// restoreActiveLayer's eviction guard below also reads it.)
const wmsByLayerName = (() => {
  const byLayer = {};
  Object.values(wmsServerConfiguration).forEach((value) => {
    if (value.disabled || !value.layer) return;
    byLayer[value.layer] = value;
  });
  return byLayer;
})();

// Every category each endpoint serves. The capabilities loop runs once per
// URL on a single representative entry (the first in config order — radar
// for meteocore, satellite for msg_fes, lightning for mtg_fd), but one
// document feeds several categories. Restoring only the representative's
// category left a stored product of any OTHER category (MTG Geo Colour on
// mtg_fd, H60B on msg_fes, the GOES / Himawari satellites on meteocore) to be
// restored by whichever endpoint of its category happened to answer AFTER its
// own server did — and when that one answered first, the pick silently waited
// out a full refresh cycle (5 min for the EUMETSAT endpoints).
const categoriesByUrl = (() => {
  const byUrl = {};
  Object.values(wmsServerConfiguration).forEach((value) => {
    if (value.disabled) return;
    if (!byUrl[value.url]) byUrl[value.url] = new Set();
    byUrl[value.url].add(value.category);
  });
  return byUrl;
})();

// Re-apply the remembered style for one category. updateLayer already carries
// it through every product switch; this covers the product NO switch touches —
// the boot default, where the layer sits on its constructor-time product and
// only GetCapabilities can say which styles it advertises.
//
// A style the current product doesn't advertise is left in the store rather
// than evicted: it is a choice about the colormap, not about this product, and
// the next product that offers it should honour it. Nothing here is persisted —
// setLayerStyle records user choices, and this only replays them.
function restoreActiveStyle(category, pane) {
  const stored = pane.ACTIVE_STYLES[category];
  if (!stored) return;
  const olLayer = pane.layerss[category];
  if (!olLayer) return;
  const info = layerInfo[olLayer.getSource().getParams().LAYERS];
  if (!info || !Array.isArray(info.style)) return;
  if (!info.style.some((s) => s.Name === stored)) return;
  if ((olLayer.getSource().getParams().STYLES || '') === stored) return;
  olLayer.setLayerStyle(stored);
}

// Restore the user's previously selected sublayer and style for one category
// (e.g. 'radarLayer') after that category's WMS GetCapabilities has populated
// layerInfo. If the stored layer is no longer advertised by the server,
// drop it — the layer stays at its constructor-time default.
function restoreActiveLayer(category, pane = pane0, respondingWms = null) {
  restoreActiveSublayer(category, pane, respondingWms);
  restoreActiveStyle(category, pane);
}

function restoreActiveSublayer(category, pane = pane0, respondingWms = null) {
  if (!category) return;
  // While a pane is drilled into a single radar site, its radar layer runs a
  // transient `<collection>/<quantity>` product that is NOT in ACTIVE_LAYERS.
  // Skip the restore so the periodic (60 s) capabilities refresh doesn't revert
  // it back to the stored composite mid-session.
  if (category === 'radarLayer' && pane.radarSite && pane.radarSite.isSingleSiteActive()) return;
  const olLayer = pane.layerss[category];
  if (!olLayer) return;
  const stored = pane.ACTIVE_LAYERS[category];
  if (!stored) return;

  if (!layerInfo[stored] || layerInfo[stored].category !== category) {
    // Same-category capabilities handlers race — the radar category is served
    // by both meteocore and EUMETSAT's H60B endpoint, and response-handler
    // order is nondeterministic: only the endpoint that OWNS the stored
    // product may evict it. A faster answer from a server that never
    // advertises the layer must not wipe the user's selection before the
    // owning server has been heard.
    const owner = wmsByLayerName[stored];
    if (respondingWms && owner && owner.url !== respondingWms.url) return;
    delete pane.ACTIVE_LAYERS[category];
    if (pane === pane0) persistActiveLayers();
    // Eviction leaves the layer on its constructor-time default, so — unlike
    // every other path out of this function — no updateLayer runs and nothing
    // re-fires the legend refresh. The boot-time seed refresh has already
    // asked the server about the (now unknown) stored product and hidden the
    // legend on the failure, so without this it stays hidden for the whole
    // session even though the default product renders fine.
    if (pane === pane0 && category === 'radarLayer' && radarLegend) radarLegend.refresh();
    return;
  }

  const currentLayers = olLayer.getSource().getParams().LAYERS;
  // In nowcast mode the mounted slot's LAYERS legitimately flips between the
  // observed and forecast product per displayed frame — the stored nowcast
  // entry is "applied" whenever the mode owns the pane's radar. Comparing
  // slot params alone would re-apply it on every 60 s refresh and churn all
  // 13 slots (sticky + flow-cache invalidation).
  const applied = currentLayers === stored
    || (category === 'radarLayer' && stored === NOWCAST_LAYER && isNowcastPane(pane));
  if (!applied) {
    updateLayer(olLayer, stored, {
      skipVisibility: true,
      skipTracking: true,
      skipPersist: true,
    });
  }
}

const featureOverlay = new VectorLayer({
  source: new Vector(),
  map,
  style() {
    return style;
  },
});
let highlight;

const displayFeatureInfo = function (pixel) {
  const feature = map.forEachFeatureAtPixel(pixel, (f) => f);

  if (feature !== highlight) {
    if (highlight) {
      featureOverlay.getSource().removeFeature(highlight);
    }
    if (feature && feature.getGeometry().getType() === 'Point') {
      featureOverlay.getSource().addFeature(feature);
    }
    highlight = feature;
  }
};

// Radar coverage overlay (range rings + radial bearings) for the radar shown in
// single-site mode, drawn on the owning pane's guide layer. Owned by that
// pane's radarSite enter/exit so the rings appear and clear together with the
// single-radar display — not on a bare marker tap.
function drawRadarCoverage(pane, feature) {
  pane.guideLayer.getSource().clear(true);
  if (!feature) return;
  const coords = transform(feature.getGeometry().getCoordinates(), pane.map.getView().getProjection(), 'EPSG:4326');
  [50000, 100000, 150000, 200000, 250000].forEach((range) => rangeRings(pane.guideLayer, coords, range));
  Array.from({ length: 360 / options.radialSpacing }, (_, index) => index * options.radialSpacing)
    .forEach((bearing) => bearingLine(pane.guideLayer, coords, 250, bearing));
}

function clearRadarCoverage(pane) {
  pane.guideLayer.getSource().clear(true);
}

// Whether the radar WMS currently advertises a layer name, fed by the
// GetCapabilities-driven layerInfo registry. Tri-state: undefined until the
// radar GetCapabilities has been parsed (the registry can't answer yet), then
// a definite yes/no. radarSite stays optimistic on undefined and only disables
// its toggle on a definite "not advertised" (down radar / not-yet-served
// station).
function isRadarLayerAdvertised(name) {
  const ready = Object.values(layerInfo).some((info) => info.category === 'radarLayer');
  return ready ? Boolean(layerInfo[name]) : undefined;
}

// One radar-site drill-in instance per pane (card + pulse overlays on the
// pane's own map, layer swaps on the pane's own radar layer), so split panes
// drill into sites independently.
function initPaneRadarSite(pane) {
  pane.radarSite = initRadarSite({
    map: pane.map,
    radarLayer: pane.layerss.radarLayer,
    radarSiteLayer: pane.radarSiteLayer,
    updateLayer,
    setTime,
    drawCoverage: (feature) => drawRadarCoverage(pane, feature),
    clearCoverage: () => clearRadarCoverage(pane),
    isLayerAdvertised: isRadarLayerAdvertised,
    // Stable product identity for the drill-in's restore target — the layer
    // source's LAYERS flips per frame in nowcast mode.
    getCanonicalProduct: () => radarProductOf(pane),
    // Only pane 0 drives the shared bottom single-radar strip.
    onSingleSiteChange: pane.index === 0
      ? (state) => { if (radarStrip) radarStrip.update(state); }
      : undefined,
  });
}

// One traffic-announcement card per pane, so a tap opens the announcement on
// the map the user actually tapped (the radar-site drill-in pattern). The
// controller and its VectorSource stay shared — only the Overlay is per-pane.
function initPaneTraffic(pane) {
  pane.traffic = trafficMessages.attachPane(pane.map, pane.trafficLayer);
  pane.weatherWarnings = weatherWarnings.attachPane(pane.map, pane.weatherWarningsLayer);
  // Cameras only need a per-pane hit-test — unlike the traffic card, the image
  // panel is a single global bottom panel, so every pane opens the same one.
  pane.cameras = weatherCameras.attachPane(pane.map, pane.weatherCameraLayer);
  // Railway stations open the departure board — also a single global bottom
  // panel, so panes share it and only the hit-test is per-pane.
  pane.trains = trains.attachPane(pane.map, pane.railwayLayer);
  // Aircraft cards follow a moving subject, so each pane keeps its own.
  pane.gliders = gliders.attachPane(pane.map, pane.gliderLayer);
  pane.trainLocations = trainLocations.attachPane(pane.map, pane.trainLocationLayer);
  pane.rescueVessels = rescueVessels.attachPane(pane.map, pane.rescueVesselLayer);
  pane.metar = metar.attachPane(pane.map, pane.metarLayer);
  // Storm cells share the strip too, and they are the first subject on it that
  // follows the clock rather than a live feed — scrubbing a frame re-reads the
  // same storm from that frame's analysis.
  pane.stormCells = stormCells.attachPane(pane.map, pane.stormCellsLayer);
  // Own position/vessel drives the same strip; only the hit-test is per-pane.
  if (ownLocation) pane.ownTelemetry = ownLocation.attachPane(pane);
  // The particle overlay canvas goes into the viewport now that the map exists.
  wind.attachPane(pane.map, pane.index);
}

// One crosshair ("Tähtäin") instance per pane: the reticle overlays the
// pane's own viewport, samples the pane's own radar canvas, and targets the
// EDR collection of whatever product that pane currently shows (composite or
// single-site drill-in).
function initPaneCrosshair(pane) {
  const layer = pane.layerss.radarLayer;
  pane.crosshair = initCrosshair({
    map: pane.map,
    radarLayer: layer,
    radarSiteSource,
    getActiveSiteLonLat: pane.radarSite.getActiveSiteLonLat,
  });
  // Seed the EDR target from the pane's current product: clonePaneDisplay
  // writes params via updateParams (not updateLayer), so no setActiveLayer
  // sync has fired for a freshly-created split pane.
  if (layer.getVisible()) {
    pane.crosshair.setActiveLayer(edrLayerFor(layer.getSource().getParams().LAYERS), {
      z: layer.getSource().getParams().ELEVATION,
    });
  }
}

function onChangeVisible(event) {
  const layer = event.target;
  const wmslayer = layer.getSource().getParams().LAYERS;
  const name = layer.get('name');
  const isVisible = layer.getVisible();
  const pane = paneOf(layer);
  const isPane0 = pane === pane0;

  if (isVisible) pane.VISIBLE.add(name); else pane.VISIBLE.delete(name);
  debug(`${isVisible ? 'Activated' : 'Deactivated'} ${name} (pane ${pane.index})`);

  // The global toolbar reflects the primary pane (pane 0). Per-pane state lives
  // on the pill.
  if (isPane0) {
    localStorage.setItem('VISIBLE', JSON.stringify([...pane.VISIBLE]));
    setButtonState(`${name}Button`, isVisible);
    updateCanonicalPage();
  }

  refreshPanePillButton(pane, name);
  recomputeAllTimelineCells();

  if (isPane0 && probe && layer === radarLayer) {
    probe.setActiveLayer(isVisible ? edrLayerFor(wmslayer) : null, {
      z: layer.getSource().getParams().ELEVATION,
    });
  }
  if (name === 'radarLayer' && pane.crosshair) {
    pane.crosshair.setActiveLayer(isVisible ? edrLayerFor(wmslayer) : null, {
      z: layer.getSource().getParams().ELEVATION,
    });
  }
  if (isPane0 && radarLegend && name === 'radarLayer') radarLegend.refresh();
  if (name === 'radarLayer') refreshLayerBbox(pane, wmslayer);
  // Turning a layer on or off changes which outages are worth mentioning —
  // and any pane's visibility counts, not just pane 0's.
  if (serverStatus) serverStatus.refresh();
  // Visibility change may invalidate the current timeline window
  // (e.g. activating a stale satellite caps `end` to its old time.end,
  // or hiding it releases the cap). Recompute window + per-layer
  // in-range state immediately so the map time, stale colour and
  // layer opacity update without waiting for a tick or manual step.
  setTime('keep');
  // Turning a pane's radar off exits its single-site mode, restoring the
  // composite (while hidden) so re-enabling shows the composite again.
  if (name === 'radarLayer' && !isVisible && pane.radarSite) {
    pane.radarSite.exitSingleSite({ restore: true });
  }
}

/**
 * Toggles the visibility of a given layer.
 * If the layer is currently visible, it will be set to invisible, and vice versa.
 *
 * @param {ol.layer} layer - The layer whose visibility will be toggled.
 */
function toggleLayerVisibility(layer, source) {
  layer.setVisible(!layer.getVisible());
  if (source) {
    trackCategory(layer, { action: 'toggle', visible: layer.getVisible(), source });
  }
}

// Whether the user has already opened a long-press menu (persisted), so the
// discovery hint in the coachmark stops appearing once the gesture is learned.
let longPressDiscovered = safeParseJSON('LP_HINT_SEEN', false);

// Toggle wrapper for segment-originated actions (button taps + keyboard
// shortcuts). Announces the Finnish layer name via coachmark only when the
// toggle turned the layer on. Deliberately not used by long-press variant
// selection so that path stays quiet.
function toggleAndAnnounce(layer, segId, source) {
  toggleLayerVisibility(layer, source);
  if (layer.getVisible()) {
    const seg = document.getElementById(segId);
    if (seg && typeof showCoachmark === 'function') {
      // Teach the long-press menu on real button taps, until the user discovers
      // it. Keyboard shortcuts (source: 'key') skip the "hold the button" hint.
      showCoachmark(seg.getAttribute('data-name'), {
        longPressHint: source === 'button' && !longPressDiscovered,
      });
    }
  }
}

//
// PER-PANE MINI CONTROLS (split mode)
//
// In split layouts each pane carries its own compact category pill so the user
// edits that pane's content directly; the global top toolbar is hidden. The
// pill reuses the shared sublayer long-press menus, targeted at the pane.
// (CATEGORY_UI / PILL_CATEGORIES are declared near the top of the module so the
// layout helpers above can reference them.)

// Resolve which pane owns an OL content layer (back-ref set in createPane).
function paneOf(layer) {
  const idx = layer.get('_paneIndex') || 0;
  return panes[idx] || pane0;
}

// Apply a sublayer pick (from a long-press menu) to one pane's category.
function applySublayerToPane(pane, category, id) {
  if (category === 'radarLayer') {
    // Exit the pane's single-site drill-in before swapping its composite;
    // updateLayer clears ELEVATION in the same params update as LAYERS.
    if (pane.radarSite) pane.radarSite.exitSingleSite({ restore: false });
    updateLayer(pane.layerss.radarLayer, id, { source: 'longpress' });
    // Reshape the shared window immediately — the nowcast entry extends it
    // into the future, and any pane can flip the mode on or off.
    setTime('keep');
    // Recentre the shared view to the picked radar's footprint only for the
    // primary pane — a background pane's pick shouldn't yank every pane around.
    if (pane === pane0) fitToLayerExtent(id);
  } else {
    updateLayer(pane.layerss[category], id, { source: 'longpress' });
    // Same primary-pane-only recentre as radar: the GOES / Himawari disks lie
    // nowhere near a view over Europe.
    if (category === 'satelliteLayer' && pane === pane0) fitToLayerExtent(id);
  }
  const menu = document.getElementById(CATEGORY_UI[category].menu);
  if (menu) menu.style.display = 'none';
}

function refreshPanePillButton(pane, category) {
  const btn = pane.pillButtons && pane.pillButtons[category];
  if (!btn) return;
  const on = pane.VISIBLE.has(category);
  btn.classList.toggle('selectedButton', on);
  btn.setAttribute('aria-pressed', String(on));
}

function refreshPanePill(pane) {
  PILL_CATEGORIES.forEach((c) => refreshPanePillButton(pane, c));
}

// Build a pane's pill (once) and wire each button: tap toggles that pane's
// category, long-press opens the shared sublayer menu targeted at the pane.
function buildPanePill(pane) {
  if (pane.pill) return;
  const pill = document.createElement('div');
  pill.className = 'pane-pill noselect';
  pane.pillButtons = {};
  PILL_CATEGORIES.forEach((category) => {
    const cfg = CATEGORY_UI[category];
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pane-seg';
    btn.setAttribute('aria-label', cfg.aria);
    const icon = document.createElement('i');
    icon.className = 'material-icons';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = cfg.icon;
    btn.appendChild(icon);
    pill.appendChild(btn);
    pane.pillButtons[category] = btn;
    createLongPressHandler(
      btn,
      cfg.menu,
      () => toggleLayerVisibility(pane.layerss[category], 'pane-pill'),
      (id) => applySublayerToPane(pane, category, id),
      () => (category === 'radarLayer'
        ? radarProductOf(pane)
        : pane.layerss[category].getSource().getParams().LAYERS),
      () => pane.layerss[category].getVisible(),
      () => { hideCoachmarkNow(); markLongPressDiscovered(); },
      (menu) => layerPanel.populate(menu, pane.layerss[category]),
    );
  });
  pane.el.appendChild(pill);
  pane.pill = pill;
  refreshPanePill(pane);
}

//
// EVENTS
//

document.getElementById('speedButton').addEventListener('mouseup', () => {
  switch (options.frameRate) {
    case options.defaultFrameRate:
      options.frameRate = options.defaultFrameRate * 2;
      break;
    case options.defaultFrameRate * 2:
      options.frameRate = options.defaultFrameRate * 0.5;
      break;
    default:
      options.frameRate = options.defaultFrameRate;
  }
  document.getElementById('speedButton').innerHTML = `${options.frameRate / options.defaultFrameRate}×`;
  stop();
  play();
  debug(`SPEED: ${options.frameRate}`);
});

document.getElementById('playButton').addEventListener('mouseup', () => {
  playstop();
});

document.getElementById('skipNextButton').addEventListener('mouseup', () => {
  skipNext();
});

document.getElementById('skipPreviousButton').addEventListener('mouseup', () => {
  skipPrevious();
});

// The four shared sublayer menus, opened from the global toolbar (1-up) OR any
// pane's mini pill (split). longPressMenuOpener() reports which button owns each
// open menu, so the outside-click closer below doesn't dismiss it when that very
// button is released after a long press.
const LP_MENU_IDS = [
  'observationLongPressMenu',
  'satelliteLongPressMenu',
  'radarLongPressMenu',
  'lightningLongPressMenu',
];

function closeLongPressMenusOutside(e) {
  LP_MENU_IDS.forEach((menuId) => {
    const menu = document.getElementById(menuId);
    if (!menu || menu.style.display === 'none') return;
    if (menu.contains(e.target)) return;
    const opener = longPressMenuOpener(menu);
    if (opener && opener.contains(e.target)) return;
    menu.style.display = 'none';
  });
}

window.addEventListener('mouseup', closeLongPressMenusOutside);
window.addEventListener('touchend', closeLongPressMenusOutside);

function setButtonState(id, active) {
  const el = document.getElementById(id);
  el.classList.toggle('selectedButton', active);
  el.setAttribute('aria-pressed', String(active));
}

function setButtonStates() {
  setButtonState('locationLayerButton', IS_TRACKING);
  setButtonState('satelliteLayerButton', VISIBLE.has('satelliteLayer'));
  setButtonState('radarLayerButton', VISIBLE.has('radarLayer'));
  setButtonState('lightningLayerButton', VISIBLE.has('lightningLayer'));
  setButtonState('observationLayerButton', VISIBLE.has('observationLayer'));
  updateThemeChipsState();
}

// Press feedback for all floating controls
document.querySelectorAll('.pill .seg, #menuButton, .location-fab').forEach((btn) => {
  function addPress() { btn.classList.add('pressing'); }
  function removePress() { btn.classList.remove('pressing'); }
  btn.addEventListener('mousedown', addPress);
  btn.addEventListener('mouseup', removePress);
  btn.addEventListener('mouseleave', removePress);
  btn.addEventListener('touchstart', addPress);
  btn.addEventListener('touchend', removePress);
  btn.addEventListener('touchcancel', removePress);
});

document.getElementById('locationLayerButton').addEventListener('mouseup', () => {
  if (IS_TRACKING) {
    IS_TRACKING = false;
    localStorage.setItem('IS_TRACKING', JSON.stringify(false));
    ownLocation.setTracking(false);
    track('tracking-off');
  } else {
    IS_TRACKING = true;
    localStorage.setItem('IS_TRACKING', JSON.stringify(true));
    ownLocation.setTracking(true);
    if (ownPosition.length > 1) {
      map.getView().setCenter(ownPosition);
    }
    track('tracking-on');
  }
  setButtonStates();
});

// Long press menus for layer buttons. Opening any menu dismisses a lingering
// discovery hint and records that the gesture has been learned.
const onLongPressDiscovered = () => { hideCoachmarkNow(); markLongPressDiscovered(); };

const observationMenu = createLongPressHandler(
  'observationLayerButton',
  'observationLongPressMenu',
  () => { toggleAndAnnounce(observationLayer, 'observationLayerButton', 'button'); },
  (id) => { updateLayer(observationLayer, id, { source: 'longpress' }); observationMenu.hide(); },
  () => observationLayer.getSource().getParams().LAYERS,
  () => observationLayer.getVisible(),
  onLongPressDiscovered,
  (menu) => layerPanel.populate(menu, observationLayer),
);

const satelliteMenu = createLongPressHandler(
  'satelliteLayerButton',
  'satelliteLongPressMenu',
  () => { toggleAndAnnounce(satelliteLayer, 'satelliteLayerButton', 'button'); },
  (id) => {
    updateLayer(satelliteLayer, id, { source: 'longpress' });
    fitToLayerExtent(id);
    satelliteMenu.hide();
  },
  () => satelliteLayer.getSource().getParams().LAYERS,
  () => satelliteLayer.getVisible(),
  onLongPressDiscovered,
  (menu) => layerPanel.populate(menu, satelliteLayer),
);

const radarMenu = createLongPressHandler(
  'radarLayerButton',
  'radarLongPressMenu',
  () => { toggleAndAnnounce(radarLayer, 'radarLayerButton', 'button'); },
  (id) => {
    // Picking a composite from the menu exits single-site mode first (clears
    // the ELEVATION param + card toggle) without restoring, since the line
    // below sets the new composite itself.
    if (radarSite) radarSite.exitSingleSite({ restore: false });
    updateLayer(radarLayer, id, { source: 'longpress' });
    // Reshape the window immediately: the nowcast entry extends it into the
    // future (and leaving the entry reverts it) — while playback is stopped
    // nothing else would re-run setTime.
    setTime('keep');
    fitToLayerExtent(id);
    radarMenu.hide();
  },
  () => radarProductOf(pane0),
  () => radarLayer.getVisible(),
  onLongPressDiscovered,
  (menu) => layerPanel.populate(menu, radarLayer),
);

const lightningMenu = createLongPressHandler(
  'lightningLayerButton',
  'lightningLongPressMenu',
  () => { toggleAndAnnounce(lightningLayer, 'lightningLayerButton', 'button'); },
  (id) => { updateLayer(lightningLayer, id, { source: 'longpress' }); lightningMenu.hide(); },
  () => lightningLayer.getSource().getParams().LAYERS,
  () => lightningLayer.getVisible(),
  onLongPressDiscovered,
  (menu) => layerPanel.populate(menu, lightningLayer),
);

// Overflow menu (three-dots) — open/close + theme chip wiring
const overflowMenuEl = document.getElementById('overflowMenu');
const overflowBackdropEl = document.getElementById('overflowMenuBackdrop');
const menuButtonEl = document.getElementById('menuButton');

function openOverflowMenu() {
  overflowMenuEl.hidden = false;
  // force reflow so the CSS transition runs from the initial state
  overflowMenuEl.getBoundingClientRect();
  overflowMenuEl.classList.add('open');
  overflowBackdropEl.classList.add('open');
  menuButtonEl.setAttribute('aria-expanded', 'true');
  updateThemeChipsState();
  updateLayoutChipsState();
  if (poiMenu) {
    // Always opens at its collapsed height: the parts are a detour, not where
    // anyone starts, and a menu that reopens the length you last left it is a
    // menu that has quietly grown.
    poiMenu.collapseAll();
    poiMenu.refresh();
  }
  if (ownLocationMenu) ownLocationMenu.refresh();
}

function closeOverflowMenu() {
  overflowMenuEl.classList.remove('open');
  overflowBackdropEl.classList.remove('open');
  menuButtonEl.setAttribute('aria-expanded', 'false');
  setTimeout(() => {
    if (!overflowMenuEl.classList.contains('open')) overflowMenuEl.hidden = true;
  }, 200);
}

menuButtonEl.addEventListener('mouseup', () => {
  if (overflowMenuEl.classList.contains('open')) closeOverflowMenu();
  else openOverflowMenu();
});

overflowBackdropEl.addEventListener('mouseup', closeOverflowMenu);

function closeOverflowIfOutside(e) {
  if (!overflowMenuEl.classList.contains('open')) return;
  if (overflowMenuEl.contains(e.target)) return;
  if (menuButtonEl.contains(e.target)) return;
  // The open backdrop (z 110) covers the whole screen, including the menu
  // button (z 100). On touch, closing here removes the backdrop, so the
  // synthesized mouseup for this same tap would then land on the now-exposed
  // button and reopen the menu. Suppress the compatibility mouse events for the
  // closing tap so the button's mouseup toggle never fires.
  if (e.cancelable) e.preventDefault();
  closeOverflowMenu();
}
window.addEventListener('mouseup', closeOverflowIfOutside);
// Mirror on touchend for touch-only devices where upstream preventDefault
// can swallow the synthesized mouseup (same pattern the long-press menus use).
window.addEventListener('touchend', closeOverflowIfOutside, { passive: false });

// Tool group (Mittaa + Pistemittaus) — the FAB doubles as a Photoshop-style
// tool group. Tapping it opens a sideways flyout AND re-arms the last-used
// tool; picking a tool in the flyout arms it (auto-disarming the other), and
// picking the active tool disarms it. Mirrors the overflow menu's
// open/close + outside-click pattern.
const toolGroupEl = document.getElementById('toolGroup');
const toolFabBtn = document.getElementById('measureFab');
const toolFlyoutEl = document.getElementById('toolFlyout');
const toolFlyoutBackdropEl = document.getElementById('toolFlyoutBackdrop');
const toolGroupIconEl = toolFabBtn ? toolFabBtn.querySelector('.tool-group-icon') : null;
const TOOL_ICONS = {
  measure: 'straighten', pistemittaus: 'colorize', crosshair: 'center_focus_weak', rengas: 'track_changes', piirto: 'gesture', poikkileikkaus: 'area_chart',
};
// Default tool the FAB arms on a plain tap (and shows in its icon) until the
// user picks another from the flyout — the centre-crosshair reticle.
let lastTool = 'crosshair';

function openToolFlyout() {
  toolFlyoutEl.hidden = false;
  toolFlyoutEl.getBoundingClientRect(); // force reflow so the transition runs
  toolFlyoutEl.classList.add('open');
  toolFlyoutBackdropEl.classList.add('open');
  toolFabBtn.setAttribute('aria-expanded', 'true');
}

function closeToolFlyout() {
  toolFlyoutEl.classList.remove('open');
  toolFlyoutBackdropEl.classList.remove('open');
  toolFabBtn.setAttribute('aria-expanded', 'false');
  setTimeout(() => {
    if (!toolFlyoutEl.classList.contains('open')) toolFlyoutEl.hidden = true;
  }, 200);
}

// Show the crosshair on every ACTIVE pane while the Tähtäin tool is armed,
// hide it everywhere else. Hiding dropped panes is mandatory, not cosmetic:
// zero-size panes still receive postrender via the shared View, so a stray
// visible crosshair would keep issuing debounced EDR refetches — hide()
// aborts its in-flight fetch and timer.
function syncCrosshairVisibility() {
  const armed = tools && tools.getActiveTool() === 'crosshair';
  panes.forEach((pane, i) => {
    if (!pane.crosshair) return;
    if (armed && i < activeCount) pane.crosshair.show();
    else pane.crosshair.hide();
  });
}

// Reflect the active tool (or the last-used tool when nothing is armed) in the
// FAB icon and the flyout items' pressed state. Passed to initTools as
// onToolChange so Esc / chip-× / mutual exclusion all keep the FAB in sync.
// (tools.js owns the FAB's tool-armed ring + aria-pressed.)
function syncToolGroup() {
  const active = tools ? tools.getActiveTool() : null;
  if (toolGroupIconEl) toolGroupIconEl.textContent = TOOL_ICONS[active || lastTool];
  if (toolFlyoutEl) {
    toolFlyoutEl.querySelectorAll('.tool-flyout-item').forEach((item) => {
      item.setAttribute('aria-pressed', item.dataset.tool === active ? 'true' : 'false');
    });
  }
  // The crosshair is a passive screen-centre overlay rather than a map-tap
  // tool, so its visibility is driven here from the single-select tool state.
  syncCrosshairVisibility();
  // Persist the tool selection across sessions. Prefer `active` over
  // `lastTool`: the flyout handler updates lastTool only after setActiveTool
  // (and this callback) returns, so lastTool is stale while arming.
  if (tools) localStorage.setItem('TOOL_STATE', JSON.stringify({ last: active || lastTool, armed: !!active }));
}

if (toolFabBtn && toolFlyoutEl) {
  // Tap toggles the active/last tool so it's usable immediately (no flyout to
  // dismiss first). Press-and-hold opens the flyout to switch tools — same
  // gesture as the app's long-press layer menus. Pointer events unify
  // mouse/touch and avoid the touch "ghost click" double-firing.
  const toggleActiveTool = () => {
    if (!tools) return;
    if (tools.getActiveTool()) tools.setActiveTool(null);
    else tools.setActiveTool(lastTool);
  };
  let pressTimer = null;
  const cancelPress = () => {
    if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; }
  };
  toolFabBtn.addEventListener('pointerdown', () => {
    cancelPress();
    pressTimer = setTimeout(() => {
      pressTimer = null; // mark the long-press as consumed
      openToolFlyout();
    }, 500);
  });
  toolFabBtn.addEventListener('pointerup', () => {
    // A pending timer means this was a short press (tap) → toggle. A null
    // timer means the long-press already fired and opened the flyout.
    if (!pressTimer) return;
    cancelPress();
    toggleActiveTool();
  });
  toolFabBtn.addEventListener('pointerleave', cancelPress);
  toolFabBtn.addEventListener('pointercancel', cancelPress);
  // Keyboard activation (Enter/Space) fires a click with detail 0 and no
  // pointer sequence; pointer-driven clicks (detail >= 1) are already handled.
  toolFabBtn.addEventListener('click', (e) => {
    if (e.detail === 0) toggleActiveTool();
  });

  toolFlyoutEl.querySelectorAll('.tool-flyout-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!tools) return;
      const { tool } = item.dataset;
      if (tools.getActiveTool() === tool) {
        tools.setActiveTool(null);
      } else {
        tools.setActiveTool(tool);
        lastTool = tool;
      }
      closeToolFlyout();
    });
  });

  if (toolFlyoutBackdropEl) toolFlyoutBackdropEl.addEventListener('click', closeToolFlyout);

  const closeToolFlyoutIfOutside = (e) => {
    if (!toolFlyoutEl.classList.contains('open')) return;
    if (toolGroupEl.contains(e.target)) return;
    closeToolFlyout();
  };
  window.addEventListener('mouseup', closeToolFlyoutIfOutside);
  window.addEventListener('touchend', closeToolFlyoutIfOutside);
}

document.querySelectorAll('#overflowMenu .chip[data-theme]').forEach((chip) => {
  chip.addEventListener('mouseup', () => {
    setUserTheme(chip.getAttribute('data-theme'));
  });
});

document.querySelectorAll('#overflowMenu .chip[data-interp]').forEach((chip) => {
  chip.addEventListener('mouseup', () => {
    if (chip.getAttribute('aria-disabled') === 'true') return;
    setInterpMode(chip.getAttribute('data-interp'));
  });
});
// Initialise chip disabled/selected state up front — before the
// capability probe resolves, interpCapable is false so only 'off'
// is tappable. Otherwise taps arrive at setInterpMode which
// silently rejects non-'off' modes, and the user sees no feedback.
updateInterpChipsState();

// Layout chips (Näkymä): 1-up / 2-up / 4-up.
document.querySelectorAll('#overflowMenu .chip[data-layout]').forEach((chip) => {
  chip.addEventListener('mouseup', () => {
    setLayout(chip.getAttribute('data-layout'));
  });
});

// POI layers — map features that users can toggle independently of data layers.
// Adding a future POI = one entry in this registry; the overflow menu row and
// localStorage persistence fall out automatically.
// `layerKeys` are pane property names — a POI toggle fans its visibility out to
// the matching layer in EVERY pane so context overlays stay consistent across
// the split.
const poiRegistry = [
  {
    // These warning filters share one layer; applyPoiVisibility combines them.
    id: 'ukkosvaroitukset',
    section: 'varoitukset',
    label: 'Ukkosvaroitukset',
    icon: 'warning_amber',
    defaultOn: false,
    layerKeys: [],
  },
  {
    id: 'tuulivaroitukset',
    section: 'varoitukset',
    label: 'Tuulivaroitukset',
    icon: 'air',
    defaultOn: false,
    layerKeys: [],
  },
  {
    id: 'sadevaroitukset',
    section: 'varoitukset',
    label: 'Sadevaroitukset',
    icon: 'water_drop',
    defaultOn: false,
    layerKeys: [],
  },
  {
    id: 'placenames',
    label: 'Nimistö',
    icon: 'label',
    defaultOn: true,
    layerKeys: ['placeNamesLayer'],
  },
  {
    id: 'radars',
    label: 'Tutka-asemat',
    icon: 'cell_tower',
    defaultOn: true,
    layerKeys: ['radarSiteLayer'],
  },
  {
    // Live data like stormcells: applyPoiVisibility drives the controller
    // (setEnabled) — the WebGL context, the frame loop and the field fetches
    // exist only while one of the two particle rows is on. They are one layer
    // with two sources, so they are `exclusive`: switching one on switches
    // the other off (togglePoi / setPoiGroup), and the layer's visibility is
    // the pair's OR, fanned out by hand in applyPoiVisibility rather than
    // through layerKeys, where the second row's `false` would win.
    id: 'tuuli',
    label: wind.modelLabel,
    icon: 'air',
    defaultOn: false,
    exclusive: 'particles',
    layerKeys: [],
  },
  {
    // The same particles over the radar's own motion field. Not wind — echo
    // motion is steering flow plus propagation — hence the name (#256).
    id: 'sateenliike',
    label: wind.radarLabel,
    icon: 'umbrella',
    defaultOn: false,
    exclusive: 'particles',
    layerKeys: [],
  },
  {
    id: 'airfields',
    section: 'ilmailu',
    label: 'Lentokentät',
    icon: 'flight',
    defaultOn: false,
    layerKeys: ['icaoLayer'],
  },
  {
    id: 'turnpoints',
    section: 'ilmailu',
    label: 'Purjelennon käännöspisteet',
    icon: 'flag',
    defaultOn: false,
    layerKeys: ['turnpointLayer'],
  },
  {
    // Live data like stormcells: applyPoiVisibility drives the WebSocket
    // (setEnabled), so nothing is connected while the layer is off.
    id: 'gliders',
    section: 'ilmailu',
    label: 'Purjekoneet',
    icon: 'airplanemode_active',
    defaultOn: false,
    layerKeys: ['gliderLayer'],
  },
  {
    // Live data: applyPoiVisibility drives the fetching (setEnabled), so nothing
    // is requested while the layer is off.
    id: 'metar',
    section: 'ilmailu',
    label: 'Sääsanomat',
    icon: 'cloud',
    defaultOn: false,
    layerKeys: ['metarLayer'],
  },
  {
    // Aviation context, and the app's only CC BY-NC dataset — see
    // scripts/fetch-airspace.mjs. Three parts because they answer different
    // questions: where you may not go, where you must talk to someone, and
    // where the military might be. The reservation areas are 378 of Finland's
    // 684 polygons (and 351 of France's 1770) and are off by default; on, at
    // synoptic zoom, they cover much of the country and bury the radar this app
    // exists to show.
    //
    // Finland and France, fetched per country as the map reaches them — the
    // gate lives in src/airspace.js and is fed from onPaneMoveEnd.
    id: 'airspace',
    section: 'ilmailu',
    label: 'Ilmatilat',
    icon: 'radar',
    defaultOn: false,
    children: [
      { id: 'controlled', label: 'Valvottu ilmatila', layerKeys: ['airspaceControlledLayer'] },
      { id: 'restricted', label: 'Rajoitusalueet', layerKeys: ['airspaceRestrictedLayer'] },
      {
        id: 'reserved',
        label: 'Varausalueet',
        layerKeys: ['airspaceReservedLayer'],
        defaultOn: false,
      },
    ],
  },
  {
    // A topic with parts. The group switch still turns the whole thing on and
    // off — a station marker with no line under it reads as a dot in a field,
    // so the default remains all-or-nothing — but the parts can be operated
    // individually from the row's disclosure. The id keeps its original
    // spelling so nobody's saved POI_STATE is reset by the rename.
    id: 'railwaystations',
    label: 'Rautatiet',
    icon: 'train',
    defaultOn: false,
    // Ordered as they stack on the map, bottom first, which is also how a
    // reader builds the picture: the network, its stations, then what is
    // running on it.
    children: [
      { id: 'tracks', label: 'Raiteet', layerKeys: ['railwayTrackLayer'] },
      { id: 'stations', label: 'Asemat', layerKeys: ['railwayLayer'] },
      // Junat is the one that costs something to leave on: it holds the MQTT
      // subscription open (see applyPoiVisibility).
      { id: 'trains', label: 'Junat', layerKeys: ['trainLocationLayer'] },
    ],
  },
  {
    id: 'municipalities',
    label: 'Kunnat',
    icon: 'location_city',
    defaultOn: false,
    layerKeys: ['municipalityLayer'],
  },
  {
    // Unlike the other POIs this one carries live data: applyPoiVisibility
    // also drives the controller's fetch/poll loop (setEnabled) so nothing is
    // requested while the layer is off.
    id: 'stormcells',
    label: 'Soluntunnistus',
    icon: 'storm',
    defaultOn: false,
    layerKeys: ['stormCellsLayer'],
  },
  {
    // Live data like stormcells: applyPoiVisibility also drives the
    // controller's fetch/poll loop (setEnabled) so nothing is requested while
    // the layer is off.
    id: 'liikennetiedotteet',
    label: 'Liikennetiedotteet',
    icon: 'traffic',
    defaultOn: false,
    layerKeys: ['trafficLayer'],
  },
  {
    // Live data like stormcells/liikennetiedotteet: applyPoiVisibility drives
    // the controller's station fetch (setEnabled) so nothing is requested while
    // the layer is off. Markers are hidden below ~z8 (see MAX_RESOLUTION) —
    // 812 cameras at synoptic zoom would bury the weather.
    id: 'kelikamerat',
    label: 'Kelikamerat',
    icon: 'photo_camera',
    defaultOn: false,
    layerKeys: ['weatherCameraLayer'],
  },
  {
    id: 'vesivaylat',
    section: 'vesiliikenne',
    label: 'Vesiväylät',
    icon: 'route',
    defaultOn: false,
    // Area fill renders behind the line geometry — both flip together
    // off the same toggle. Order here doesn't drive z-order; the layers
    // array does (vesivaylaAreaLayer sits before vesivaylatLayer there).
    layerKeys: ['vesivaylaAreaLayer', 'vesivaylatLayer'],
  },
  {
    // Live data like the aircraft and the trains: applyPoiVisibility drives the
    // AIS subscription (setEnabled), so nothing is connected while it is off.
    id: 'pelastusalukset',
    section: 'vesiliikenne',
    label: 'Pelastusalukset',
    icon: 'support',
    defaultOn: false,
    layerKeys: ['rescueVesselLayer'],
  },
];

// A topic's part is stored under `<topic>.<part>`, flat, so POI_STATE stays a
// map of booleans and the reconciliation below stays one pass.
const poiChildKey = (entry, child) => `${entry.id}.${child.id}`;

// Sections group topics in the menu and nothing else. A section has NO state of
// its own: whether it looks on, off or half-on is read from its members, and
// pressing it just operates them. That is what keeps this a rendering concern —
// POI_STATE, applyPoiVisibility and every topic's own parts are untouched by it,
// and a topic does not care whether it happens to be displayed inside one.
const poiSections = {
  varoitukset: { label: 'Varoitukset', icon: 'warning_amber' },
  ilmailu: { label: 'Ilmailu', icon: 'flight_takeoff' },
  vesiliikenne: { label: 'Vesiliikenne', icon: 'directions_boat' },
};

const poiSectionMembers = (sectionId) => poiRegistry.filter((e) => e.section === sectionId);

// POI_STATE is reconciled against the current registry on every load: unknown
// persisted keys are dropped, and new registry entries fall back to defaultOn.
const POI_STATE = (() => {
  const persisted = safeParseJSON('POI_STATE', null) || {};
  const has = (k) => Object.prototype.hasOwnProperty.call(persisted, k);
  const state = {};
  poiRegistry.forEach((entry) => {
    state[entry.id] = has(entry.id) ? !!persisted[entry.id] : entry.defaultOn;
    // Parts default to ON unless the part says otherwise. A topic that has
    // never been split before was all-or-nothing, so everyone's saved
    // `railwaystations: true` has to keep meaning all three — the group switch
    // alone decides whether anything shows. A part may still opt out
    // (`defaultOn: false`) when it is too heavy to be a sensible default, which
    // is how the airspace reservation areas start off.
    (entry.children || []).forEach((child) => {
      const key = poiChildKey(entry, child);
      const fallback = child.defaultOn !== false;
      state[key] = has(key) ? !!persisted[key] : fallback;
    });
  });
  return state;
})();

// Is this layer actually on: the topic switched on AND, for a topic with parts,
// that part switched on too.
function isPoiOn(entryId, childId) {
  if (!POI_STATE[entryId]) return false;
  return childId ? !!POI_STATE[`${entryId}.${childId}`] : true;
}

function persistPoiState() {
  localStorage.setItem('POI_STATE', JSON.stringify(POI_STATE));
}

function applyPoiVisibility() {
  // Safety net for the `exclusive` groups (persisted state, gestures that do
  // not call applyPoiExclusive themselves).
  enforceExclusive(poiRegistry, POI_STATE);
  poiRegistry.forEach((entry) => {
    const groupOn = !!POI_STATE[entry.id];
    // A topic is either one set of layers or several named parts, each gated by
    // the group as well as by itself.
    const groups = entry.children
      ? entry.children.map((c) => ({ on: groupOn && !!POI_STATE[poiChildKey(entry, c)], keys: c.layerKeys }))
      : [{ on: groupOn, keys: entry.layerKeys }];
    for (const pane of panes) {
      groups.forEach(({ on, keys }) => keys.forEach((key) => {
        if (pane[key]) pane[key].setVisible(on);
      }));
    }
  });
  // Storm cells poll a live API — the toggle gates fetching, not just paint.
  stormCells.setEnabled(!!POI_STATE.stormcells);
  // The particle rows: one layer, two exclusive sources. Off before the
  // source switch (or switching the radar row off would start a model
  // metadata fetch only to abort it a line later); on after it, so a switch
  // while on refetches rather than keeping the other field flowing.
  const particlesOn = !!POI_STATE.tuuli || !!POI_STATE.sateenliike;
  if (!particlesOn) wind.setEnabled(false);
  wind.setSource(POI_STATE.sateenliike ? 'radar' : 'model');
  if (particlesOn) wind.setEnabled(true);
  for (const pane of panes) pane.windLayer.setVisible(particlesOn);
  trafficMessages.setEnabled(!!POI_STATE.liikennetiedotteet);
  const warningTypes = [
    POI_STATE.tuulivaroitukset && 1, POI_STATE.ukkosvaroitukset && 3, POI_STATE.sadevaroitukset && 10,
  ].filter(Boolean);
  weatherWarnings.setTypes(warningTypes);
  for (const pane of panes) pane.weatherWarningsLayer.setVisible(warningTypes.length > 0);
  weatherCameras.setEnabled(!!POI_STATE.kelikamerat);
  gliders.setEnabled(!!POI_STATE.gliders);
  // Rautatiet carries a live feed too, and it is the Junat part that holds it:
  // the toggle gates the MQTT subscription, not just whether the markers paint,
  // so switching Junat off alone must also close the socket.
  trainLocations.setEnabled(isPoiOn('railwaystations', 'trains'));
  // Airspace is a bundled file, and the toggle gates fetching it: most visitors
  // never switch this on and should not pay 254 kB for it.
  airspace.setEnabled(!!POI_STATE.airspace);
  // The AIS subscription lives only while the layer is on.
  rescueVessels.setEnabled(!!POI_STATE.pelastusalukset);
  metar.setEnabled(!!POI_STATE.metar);
}

// A gesture that turned `id` on: its `exclusive` siblings go off (poiMenu.js
// enforceExclusive). Called after the state change and before
// applyPoiVisibility.
function applyPoiExclusive(id) {
  if (POI_STATE[id]) enforceExclusive(poiRegistry, POI_STATE, id);
}

// Pressing the ROW is the remembering toggle: it hides or shows a topic without
// forgetting which of its parts you had chosen. Coming back on therefore returns
// exactly the selection you left, not all of it.
//
// The one thing it has to special-case is a topic with nothing left in it —
// switching that "on" would light the switch above an empty map, so the parts
// come back with it.
function togglePoi(id) {
  const entry = poiRegistry.find((e) => e.id === id);
  const children = (entry && entry.children) || [];
  POI_STATE[id] = !POI_STATE[id];
  if (POI_STATE[id] && children.length
    && children.every((c) => !POI_STATE[poiChildKey(entry, c)])) {
    children.forEach((c) => { POI_STATE[poiChildKey(entry, c)] = true; });
  }
  applyPoiExclusive(id);
  applyPoiVisibility();
  persistPoiState();
  if (poiMenu) poiMenu.refresh();
  track('poi-toggle', { id, visible: POI_STATE[id] });
}

// Pressing the SWITCH itself is absolute, by the side pressed: its left end
// means none of this topic, its right end means all of it. That is what makes
// the three positions real controls rather than just a readout — the knob
// sitting mid-travel can be pushed either way — and it is the only way to say
// "all of it" without opening the topic and ticking the rest by hand.
//
// Deliberately NOT a toggle: pressing the end a switch already sits at does
// nothing, which is the point. The remembering toggle is the rest of the row.
function setPoiGroup(id, on) {
  const entry = poiRegistry.find((e) => e.id === id);
  const children = (entry && entry.children) || [];
  POI_STATE[id] = on;
  if (on) children.forEach((c) => { POI_STATE[poiChildKey(entry, c)] = true; });
  applyPoiExclusive(id);
  applyPoiVisibility();
  persistPoiState();
  if (poiMenu) poiMenu.refresh();
  track('poi-toggle', { id, visible: on });
}

function togglePoiChild(entryId, childId) {
  const key = `${entryId}.${childId}`;
  POI_STATE[key] = !POI_STATE[key];
  // Operating a part is a statement about the topic too: ticking one while the
  // topic is off is a request to see it, and unticking the last one is a
  // request to see none of it. Without this the switch and the map disagree.
  const entry = poiRegistry.find((e) => e.id === entryId);
  if (entry && entry.children) {
    const anyOn = entry.children.some((c) => POI_STATE[poiChildKey(entry, c)]);
    POI_STATE[entryId] = anyOn;
  }
  applyPoiVisibility();
  persistPoiState();
  if (poiMenu) poiMenu.refresh();
  track('poi-toggle', { id: key, visible: POI_STATE[key] });
}

// `action` is 'toggle' (the row), 'all' (the switch's right end) or 'none' (its
// left end) — the same three gestures a topic with parts already answers to.
function setPoiSection(sectionId, action) {
  const members = poiSectionMembers(sectionId);
  const anyOn = members.some((e) => !!POI_STATE[e.id]);
  let target = !anyOn;
  if (action === 'all') target = true;
  if (action === 'none') target = false;
  members.forEach((entry) => {
    POI_STATE[entry.id] = target;
    const children = entry.children || [];
    if (!target || !children.length) return;
    // Turning a section on restores a topic's parts only when it has none left,
    // exactly as pressing that topic's own row would. Asking for ALL of it is
    // the one gesture that overrides a part the user switched off.
    const allOff = children.every((c) => !POI_STATE[poiChildKey(entry, c)]);
    if (action === 'all' || allOff) {
      children.forEach((c) => { POI_STATE[poiChildKey(entry, c)] = true; });
    }
  });
  applyPoiVisibility();
  persistPoiState();
  if (poiMenu) poiMenu.refresh();
  track('poi-toggle', { id: sectionId, visible: target });
}

poiMenu = initPoiMenu({
  container: document.getElementById('poiList'),
  registry: poiRegistry,
  sections: poiSections,
  isOn: (id) => !!POI_STATE[id],
  isChildOn: (entryId, childId) => !!POI_STATE[`${entryId}.${childId}`],
  onToggle: togglePoi,
  onSetGroup: setPoiGroup,
  onToggleChild: togglePoiChild,
  onSection: setPoiSection,
});

// Reconcile persisted state against layer visibility (handles the case where
// persisted state diverges from the layer's declared default), then render the
// menu rows once. Also re-persists cleaned state so stale keys drop from
// localStorage the first time a user opens the page after a registry change.
applyPoiVisibility();
persistPoiState();

// Coachmark: briefly surfaces the Finnish layer name after it becomes visible,
// so icon-only segments stay learnable. When the long-press menu hasn't been
// discovered yet, the toast also teaches the hold gesture for ~3 s; the hint
// stops appearing once any long-press menu has been opened (see LP_HINT_SEEN).
const coachmarkEl = document.getElementById('coachmark');
const coachmarkTitleEl = coachmarkEl.querySelector('.coach-title');
const coachmarkHintEl = coachmarkEl.querySelector('.coach-hint');
const LONG_PRESS_HINT_TEXT = 'Pidä pohjassa → lisää vaihtoehtoja';
let coachmarkTimer = null;

function markLongPressDiscovered() {
  if (longPressDiscovered) return;
  longPressDiscovered = true;
  localStorage.setItem('LP_HINT_SEEN', JSON.stringify(true));
}

function showCoachmark(text, { longPressHint = false } = {}) {
  if (!text) return;
  coachmarkTitleEl.textContent = text;
  coachmarkHintEl.textContent = longPressHint ? LONG_PRESS_HINT_TEXT : '';
  coachmarkHintEl.hidden = !longPressHint;
  coachmarkEl.hidden = false;
  coachmarkEl.getBoundingClientRect();
  coachmarkEl.classList.add('show');
  if (coachmarkTimer) clearTimeout(coachmarkTimer);
  coachmarkTimer = setTimeout(() => {
    coachmarkEl.classList.remove('show');
    setTimeout(() => {
      if (!coachmarkEl.classList.contains('show')) coachmarkEl.hidden = true;
    }, 220);
  }, longPressHint ? 3000 : 1400);
}

// Immediately tear down the coachmark — used when a long-press succeeds so the
// "hold the button" hint doesn't linger after the gesture has been performed.
function hideCoachmarkNow() {
  if (coachmarkTimer) clearTimeout(coachmarkTimer);
  coachmarkEl.classList.remove('show');
  coachmarkEl.hidden = true;
}

// Keystrokes aimed at a form field (e.g. the MMSI input) must never trigger
// the global shortcuts — typing "230…" would toggle layers on every digit.
function isTextEntryTarget(target) {
  return !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
}

document.addEventListener('keyup', (event) => {
  if (event.defaultPrevented || isTextEntryTarget(event.target)) {
    return;
  }

  const key = event.key || event.keyCode;
  let handled = true;
  if (key === ' ' || key === 'Space' || key === 32) {
    skipNext();
  } else if (key === ',' || key === 'Comma') {
    skipPrevious();
  } else if (key === '.' || key === 'Period') {
    skipNext();
  } else if (key === 'j' || key === 'KeyJ') {
    skipPrevious();
  } else if (key === 'k' || key === 'KeyK') {
    playstop();
  } else if (key === 'l' || key === 'KeyL') {
    skipNext();
  } else if (key === '1' || key === 'Digit1') {
    toggleAndAnnounce(satelliteLayer, 'satelliteLayerButton', 'key');
  } else if (key === '2' || key === 'Digit2') {
    toggleAndAnnounce(radarLayer, 'radarLayerButton', 'key');
  } else if (key === '3' || key === 'Digit3') {
    toggleAndAnnounce(lightningLayer, 'lightningLayerButton', 'key');
  } else if (key === '4' || key === 'Digit4') {
    toggleAndAnnounce(observationLayer, 'observationLayerButton', 'key');
  } else if ((key === 'f' || key === 'KeyF')
    && !event.ctrlKey && !event.metaKey && !event.altKey) {
    // Bare `f` only — don't hijack Cmd/Ctrl+F (find-in-page) etc.
    setAppFullscreen(!appFullscreen);
  } else if (event.key === 'Escape') {
    // Blurred-input case only: while the search input has focus, its own
    // keydown handler closes the overlay and this listener never sees the
    // key (isTextEntryTarget guard above).
    if (placeSearch && placeSearch.isOpen()) placeSearch.close();
    else if (overflowMenuEl.classList.contains('open')) closeOverflowMenu();
    else if (appFullscreen) setAppFullscreen(false);
    else handled = false;
  } else if (event.key === 'Control') {
    document.getElementById('help').style.display = 'none';
  } else if (event.key === 'Home') {
    stop();
    setTime('last');
  } else {
    handled = false;
    debug(event);
  }

  if (handled) {
    event.preventDefault();
  }
});

// One record per started capabilities loop (keyed on the wms config entry
// the dedup in `main` selected). Tracks the pending reschedule timer, the
// last attempt time and in-flight state so the visibilitychange catch-up
// below can fire an immediate refresh without ever doubling a loop.
const capsLoops = new Map(); // wms entry -> { timer, lastAttemptAt, inFlight }
function capsLoopOf(wms) {
  let loop = capsLoops.get(wms);
  if (!loop) {
    loop = { timer: 0, lastAttemptAt: 0, inFlight: false };
    capsLoops.set(wms, loop);
  }
  return loop;
}

function hostOfUrl(url) {
  try { return new URL(url).host; } catch (e) { return null; }
}

// Which categories a down host is actually costing the user right now — the
// outage banner only speaks about layers that are switched on somewhere.
//
// Answered from each layer's SOURCE URL rather than layerInfo: the source url
// is set at construction and by updateLayer, so this is still right before
// GetCapabilities has ever succeeded. Reading layerInfo instead would go blank
// exactly when the app boots into an outage — the case that matters most.
//
// The EDR-backed vector layers (observations, FMI lightning) have no source
// url; a meteocore outage still surfaces through its radar WMS, which is the
// same host.
function visibleCategoriesForHost(host) {
  const out = new Set();
  for (const pane of activePanes()) {
    for (const name of pane.VISIBLE) {
      const olLayer = pane.layerss[name];
      const source = olLayer && olLayer.getSource();
      const url = source && source.getUrl && source.getUrl();
      if (url && hostOfUrl(url) === host) out.add(name);
    }
    // The lightning category's EUMETSAT companion raster lives outside layerss.
    if (pane.VISIBLE.has('lightningLayer') && pane.lightningWmsLayer) {
      const url = pane.lightningWmsLayer.getSource().getUrl();
      if (url && hostOfUrl(url) === host) out.add('lightningLayer');
    }
  }
  return [...out];
}

// Age of the freshest frame the affected categories can still show, for the
// banner's "uusin kuva 25 min vanha". Capabilities are frozen at whatever the
// server last advertised, which is precisely the number the user wants.
function newestFrameAgeMs(categories) {
  const ends = [];
  activePanes().forEach((pane) => {
    categories.filter((name) => pane.VISIBLE.has(name)).forEach((name) => {
      const olLayer = pane.layerss[name];
      // timeLayerFor: in nowcast mode the mounted slot's LAYERS flips per frame;
      // the age must come from the observed feed, not the +2 h forecast end.
      const wmslayer = olLayer && timeLayerFor(olLayer.getSource().getParams().LAYERS);
      const info = wmslayer && layerInfo[wmslayer] && layerInfo[wmslayer].time;
      if (info && Number.isFinite(info.end)) ends.push(info.end);
    });
  });
  return ends.length ? Date.now() - Math.max(...ends) : null;
}

// "Yritä uudelleen": drop the pending backoff timer for every loop on a down
// host and poll again now. Backoff caps at 5 minutes, so without this a user
// staring at a recovered server waits out a timer that knows nothing about it.
function retryHosts(hostList) {
  capsLoops.forEach((loop, wms) => {
    if (!hostList.includes(hostOfUrl(wms.url)) || loop.inFlight) return;
    clearTimeout(loop.timer);
    loop.timer = 0;
    getWMSCapabilities(wms);
  });
}

function getWMSCapabilities(wms, failCountArg = 0) {
  const loop = capsLoopOf(wms);
  // Clearing the pending timer guards against a visibilitychange poke (or
  // any future manual caller) racing an already-scheduled run.
  if (loop.timer) {
    clearTimeout(loop.timer);
    loop.timer = 0;
  }
  loop.lastAttemptAt = Date.now();
  loop.inFlight = true;
  let failCount = failCountArg;
  const parser = new WMSCapabilities();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => { controller.abort(); }, 30000);
  debug(`Request WMS Capabilities ${wms.url}`);

  fetch(`${wms.url}?SERVICE=WMS&version=1.3.0&request=GetCapabilities`, {
    signal: controller.signal,
  }).then((response) => {
    // An HTTP error (typically 5xx from an overloaded WMS server)
    // still resolves the fetch — without this check the error body
    // flows on to .text()/parser.read, fails the structure check
    // silently, and .finally reschedules at the flat full rate. The
    // exponential backoff below only ever engages via .catch, so
    // throwing here is what routes 4xx/5xx into the backoff.
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }).then((text) => {
    clearTimeout(timeoutId);
    debug(`Received WMS Capabilities ${wms.url}`);
    failCount = 0;
    const result = parser.read(text);
    if (result && result.Capability && result.Capability.Layer && result.Capability.Layer.Layer) {
      if (serverStatus) serverStatus.report(wms.url, true);
      // Per-side GetMap limit (<MaxWidth>/<MaxHeight>) for the request shaping.
      setGetMapLimits(wms.url, result.Service);
      // A server "supports webp" when its GetCapabilities advertises
      // image/webp as a GetMap output format (e.g. meteocore). Prefer it
      // there to shrink GetMap payloads — and let servers that don't
      // advertise it (the GeoServer endpoints) keep png/jpeg.
      const getMap = result.Capability.Request && result.Capability.Request.GetMap;
      const supportsWebp = !!(getMap && Array.isArray(getMap.Format)
        && getMap.Format.includes('image/webp'));
      getLayers(result.Capability.Layer.Layer, wms, supportsWebp);
      debug(layerInfo);
      // Set each visible category's `info` and adopt webp (where advertised)
      // for every active pane's layers, including the boot defaults.
      for (const pane of activePanes()) {
        for (const name of ['satelliteLayer', 'radarLayer', 'lightningLayer', 'observationLayer']) {
          const olLayer = pane.layerss[name];
          // The radar layer's canonical product, not the mounted slot's
          // LAYERS: in nowcast mode the latter flips per displayed frame,
          // which would make the layer panel's title/info flap.
          const infoName = name === 'radarLayer'
            ? radarProductOf(pane)
            : olLayer.getSource().getParams().LAYERS;
          olLayer.set('info', layerInfo[infoName]);
          applyWireFormat(olLayer);
          if (name === 'radarLayer') refreshLayerBbox(pane, infoName);
        }
        // The lightning WMS companion carries the actual EUMETSAT raster;
        // adopt webp/format for it too (the facade's applyWireFormat is a
        // no-op on the vector source).
        if (pane.lightningWmsLayer) {
          const companion = pane.lightningWmsLayer;
          companion.set('info', layerInfo[companion.getSource().getParams().LAYERS]);
          applyWireFormat(companion);
        }
      }
      for (const category of categoriesByUrl[wms.url] || [wms.category]) {
        for (const pane of activePanes()) restoreActiveLayer(category, pane, wms);
      }
      // Hide the nowcast menu entry while the forecast product isn't
      // advertised (server down / product removed). display, not [hidden]:
      // the menu-item rules set display themselves.
      const nowcastMenuItem = document.querySelector('#radarLongPressMenu .menu-item[data-layer="fmi-radar-nowcast"]');
      if (nowcastMenuItem) {
        nowcastMenuItem.style.display = layerInfo[NOWCAST_LAYER] ? '' : 'none';
      }
      if (IS_FOLLOWING) {
        setTime('last');
      }
      // Nowcast model-run rollover for stopped/scrubbed users: the slot
      // providers read the newest advertised run live, so a key re-check is
      // all that's needed. No-op for pools whose override keys are unchanged
      // (including everyone with the mode off).
      for (const pane of activePanes()) {
        for (const poolName of Object.keys(pane.framePools)) {
          const pool = pane.framePools[poolName];
          if (pool) pool.refreshSlotOverrides();
        }
      }
    } else {
      // A 200 carrying something that isn't a capabilities document is a
      // broken server, not a working one — count it as down for the banner.
      if (serverStatus) serverStatus.report(wms.url, false);
      debug(`Invalid WMS Capabilities response structure for ${wms.url}`);
      debug(result);
    }
  }).catch((error) => {
    clearTimeout(timeoutId);
    if (serverStatus) serverStatus.report(wms.url, false);
    failCount++;
    debug(`Error fetching WMS Capabilities from ${wms.url}: ${error.message} (fail #${failCount})`);
  })
    .finally(() => {
    // Exponential backoff on failure: refresh, 2x, 4x, max 5 min
      const delay = failCount > 0
        ? Math.min(wms.refresh * 2 ** failCount, 300000)
        : wms.refresh;
      loop.inFlight = false;
      loop.timer = setTimeout(() => { getWMSCapabilities(wms, failCount); }, delay);
    });
}

// Wake-from-sleep catch-up: a hidden/frozen tab throttles or suspends the
// capabilities timers, so after a long sleep the window anchor — and in
// nowcast mode the pinned model run, which the server retains for only the
// two newest generations — can be minutes stale; every forecast frame would
// then 400 until the next scheduled refresh fired. On return to visible,
// immediately re-run every loop whose last attempt is older than its own
// cadence (a brief tab switch refreshes nothing), clearing its pending
// timer so the loop is never doubled. In-flight fetches are left alone.
// The response handler then does the rest as usual: restoreActiveLayer,
// refreshSlotOverrides (run rollover), setTime('last') while following.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  capsLoops.forEach((loop, wms) => {
    if (loop.inFlight || Date.now() - loop.lastAttemptAt < wms.refresh) return;
    getWMSCapabilities(wms);
  });
});

function getLayers(parentlayer, wms, supportsWebp = false) {
  const products = {};
  parentlayer.forEach((layer) => {
    if (Array.isArray(layer.Layer)) {
      getLayers(layer.Layer, wms, supportsWebp);
    } else {
      const name = layer.Name;
      const candidate = wmsByLayerName[name];
      const ownerWms = (candidate && candidate.url === wms.url) ? candidate : wms;
      layerInfo[name] = getLayerInfo(layer, ownerWms, supportsWebp);
      layerInfo[name].layer = name;
    }
  });
  return products;
}

function getLayerInfo(layer, wms, supportsWebp = false) {
  const product = {
    category: wms.category,
    url: wms.url,
    layer: layer.Name,
    // Whether the serving WMS advertised image/webp in its
    // GetCapabilities — resolveFormat prefers it over the default.
    webp: supportsWebp,
  };

  if (typeof layer.CRS !== 'undefined') {
    [product.crs] = layer.CRS;
  } else {
    product.crs = 'EPSG:4326';
  }

  // Geographic coverage advertised in GetCapabilities, as
  // [minLon, minLat, maxLon, maxLat] in EPSG:4326. Used to pan/zoom the
  // map to a radar source's footprint when it's picked from the radar
  // long-press menu (see fitToLayerExtent).
  if (Array.isArray(layer.EX_GeographicBoundingBox)) {
    product.bbox = layer.EX_GeographicBoundingBox;
  }

  if (typeof wms.title !== 'undefined') {
    product.title = wms.title;
  } else {
    product.title = layer.Title;
  }

  if (typeof wms.abstract !== 'undefined') {
    product.abstract = wms.abstract;
  } else {
    product.abstract = layer.Abstract;
  }

  if (typeof layer.Attribution !== 'undefined') {
    product.attribution = layer.Attribution;
  } else if (typeof wms.attribution !== 'undefined') {
    product.attribution = { Title: wms.attribution };
  }

  if (typeof wms.license !== 'undefined') {
    product.license = wms.license;
  }

  // Per-layer wire-format overrides — used by sparse overlay products
  // (e.g. MSG RDT) that need PNG + transparency on a category whose
  // default is opaque JPEG.
  if (typeof wms.format !== 'undefined') {
    product.format = wms.format;
  }
  if (typeof wms.transparent !== 'undefined') {
    product.transparent = wms.transparent;
  }

  if (typeof layer.Dimension !== 'undefined') {
    product.time = getTimeDimension(layer.Dimension);
    // Model-run dimension (fmi-radar-nowcast and some NWP layers). OL's
    // parser already delivers every Dimension element; only `time` was
    // consumed until nowcast mode needed the run list.
    product.referenceTime = parseReferenceTimes(layer.Dimension);
  }

  if (typeof layer.Style !== 'undefined') {
    product.style = layer.Style;
  }
  return product;
}

function getTimeDimension(dimensions) {
  // var time = {}
  let beginTime;
  let endTime;
  let resolutionTime;
  let prevtime;
  let defaultTime;

  dimensions.forEach((dimension) => {
    if (dimension.name === 'time') {
      defaultTime = dimension.default ? dayjs(dimension.default).valueOf() : NaN;
      dimension.values.split(',').forEach((times) => {
        const time = times.split('/');
        // Time dimension is list of times separated by comma
        if (time.length === 1) {
          // var timeValue = dayjs(time[0]).valueOf()
          const timeValue = dayjs(new Date(time[0])).valueOf();
          // begin time is the smallest of listed times
          beginTime = beginTime || timeValue;
          beginTime = Math.min(beginTime, timeValue);
          // end time is the bigest of listed times
          endTime = endTime || timeValue;
          endTime = Math.max(endTime, timeValue);
          // resolution is the difference of the last two times listed
          resolutionTime = prevtime ? (timeValue - prevtime) : 3600000;
          prevtime = timeValue;
        } else if (time.length === 3) {
          // Time dimension is starttime/endtime/period
          beginTime = dayjs(time[0]).valueOf();
          endTime = dayjs(time[1]).valueOf();
          resolutionTime = dayjs.duration(time[2]).asMilliseconds();
        }
      }); // forEach
    } // if
  }); // forEach
  const currentTime = new Date().getTime();
  const type = endTime > currentTime ? 'for' : 'obs';
  // console.log("start: " + beginTime + " end: " + endTime + " resolution: " + resolutionTime + " type: " + type + " default: " + defaultTime)
  return {
    start: beginTime, end: endTime, resolution: resolutionTime, type, default: defaultTime,
  };
}

// Build the FramePools for a pane's WMS raster layers and wire each pool's
// load/flow callbacks into the shared timeline aggregation. The EDR vector
// layers (observations, FMI lightning) hold their whole window client-side —
// their controllers are routed in setTime instead; the lightning category's
// WMS products (li_afa/rdt) animate via the companion layer's pool. For
// pane 0 `pane.framePools` is the module-global `framePools`, so this is
// identical to the old inline construction on the single-map path.
function buildPanePools(pane) {
  const pairs = [
    ['satelliteLayer', pane.layerss.satelliteLayer],
    ['radarLayer', pane.layerss.radarLayer],
    ['lightningWmsLayer', pane.lightningWmsLayer],
  ];
  for (const [name, layer] of pairs) {
    const key = `${pane.index}:${name}`;
    poolLoadStates[key] = new Array(FRAME_COUNT).fill(false);
    poolFlowStates[key] = new Array(FRAME_COUNT).fill(false);
    const pool = new FramePool({ primaryLayer: layer, map: pane.map });
    pool.onLoadStateChange = (idx, loaded) => {
      poolLoadStates[key][idx] = loaded;
      updateTimelineCell(idx);
    };
    pool.onFlowStateChange = (idx, ready) => {
      poolFlowStates[key][idx] = ready;
      updateTimelineCell(idx);
    };
    // Nowcast-mode slot providers (src/nowcast.js). Installed permanently on
    // every pane; they self-deactivate whenever nowcastBoundaryMs is null
    // (no pane in the mode), so classic behavior is untouched.
    if (name === 'radarLayer') {
      pool.setSlotParamsProvider(makeRadarSlotProvider({
        isNowcastPane: () => isNowcastPane(pane),
        getBoundaryMs: getNowcastBoundaryMs,
        getNowcastRefMs: () => newestReferenceMs(layerInfo[NOWCAST_LAYER]),
      }));
    } else {
      pool.setSlotParamsProvider(makeCoverageSlotProvider({
        getBoundaryMs: getNowcastBoundaryMs,
      }));
    }
    pane.framePools[name] = pool;
  }
}

//
// MAIN
//
const main = () => {
  timeline = new Timeline(FRAME_COUNT, document.getElementById('timeline'), {
    // Tap/drag the strip to seek — pause playback (like the skip buttons) then
    // jump to that frame within the current window.
    onSeek: (index) => { stop(); setTime('seek', index); },
  });

  setMapLayer(getEffectiveTheme());

  // Multiple wms entries can share an endpoint — every radar nation served
  // from meteocore.app.meteo.fi/wms points at the same GetCapabilities
  // document. Fetch each unique URL exactly once; getLayers populates
  // layerInfo for every layer the server advertises, so the remaining
  // entries' product names resolve naturally.
  //
  // Plain null-prototype object used as a string-keyed set of endpoints.
  const seenEndpoints = Object.create(null);
  Object.values(options.wmsServerConfiguration).forEach((value) => {
    if (value.disabled) return;
    if (!(value.url in seenEndpoints)) seenEndpoints[value.url] = value;
  });

  // EDR-backed products (former wms-obs GeoServer, offline for good) have no
  // GetCapabilities: seed their layerInfo statically and run the
  // observation-product restore that used to piggyback on the capabilities
  // response. Lightning is NOT restored here — its stored value can be a
  // WMS product (li_afa/rdt) whose layerInfo arrives with the EUMETSAT
  // capabilities, and that response triggers the category restore itself.
  Object.assign(layerInfo, edrLayerInfo);
  for (const pane of activePanes()) restoreActiveLayer('observationLayer', pane);

  Object.values(seenEndpoints).forEach((wms) => getWMSCapabilities(wms));

  setButtonStates();

  buildPanePools(pane0);
  // Pools are now in framePools. If the capability probe has already
  // resolved, wire interpolators up now; otherwise the probe's .then
  // will call attachInterpolators once the verdict lands.
  attachInterpolators();
  recomputeAllTimelineCells();

  probe = initProbe({
    container: document.getElementById('probeChart'),
    onValueChange: (v) => { if (tools) tools.setProbeValue(v); },
  });
  if (radarLayer.getVisible()) {
    probe.setActiveLayer(edrLayerFor(radarLayer.getSource().getParams().LAYERS));
  }

  rangeCircle = initRangeCircle({ onStrokeEnd: onDrawStrokeEnd });
  rangeCircle.attachPane(map);
  freehand = initFreehand({ onStrokeEnd: onDrawStrokeEnd });
  freehand.attachPane(map);
  crossSection = initCrossSection({
    container: document.getElementById('sectionPanel'),
    radarSiteSource,
    projection: map.getView().getProjection(),
    isLayerAdvertised: isRadarLayerAdvertised,
    onRequestClose: () => { if (tools) tools.setActiveTool(null); },
  });
  sectionLine = initSectionLine({
    onLineChange: (line) => { if (crossSection) crossSection.setLine(line); },
    onStrokeEnd: onDrawStrokeEnd,
  });
  sectionLine.attachPane(map);

  tools = initTools({
    map,
    getOwnPosition: () => ownPosition4326,
    getFrameTimestamp: () => (startDate ? startDate.getTime() : Date.now()),
    onPinChange: (lonLat) => probe && probe.setPin(lonLat),
    onToolChange: syncToolGroup,
    rangeCircle,
    freehand,
    sectionLine,
  });
  // Bottom single-radar strip (site · sweep · quantity · close). Created before
  // pane 0's radarSite so its onSingleSiteChange has something to update; the
  // strip's edits route back into pane 0's radarSite (set on the next line).
  radarStrip = initRadarStrip({
    element: document.getElementById('radarStrip'),
    onSelectElevation: (a) => { if (radarSite) radarSite.setActiveElevation(a); },
    onSelectQuantity: (q) => { if (radarSite) radarSite.setActiveQuantity(q); },
    onExit: () => { if (radarSite) radarSite.exitSingleSite({ restore: true }); },
  });
  // Colour-scale legend under the timeline. The getter hands over pane-0 truth
  // on every refresh; layers via radarProductOf, never getParams().LAYERS (the
  // nowcast mode flips the mounted slot's LAYERS per displayed frame).
  radarLegend = initRadarLegend({
    strip: document.getElementById('legendStrip'),
    panel: document.getElementById('legendPanel'),
    getActiveRadar: () => ({
      url: radarLayer.getSource().getUrl(),
      layers: radarProductOf(pane0),
      style: radarLayer.getSource().getParams().STYLES || '',
      visible: radarLayer.getVisible(),
    }),
  });
  radarLegend.refresh(); // seed from restored state; capabilities-driven updateLayer re-refreshes

  // Backend-outage banner. Wired before the capabilities loops start below so
  // the very first poll outcome is reported — booting straight into an outage
  // is the case it most needs to catch.
  serverStatus = initServerStatus({
    container: document.getElementById('serverStatusBanner'),
    getAffectedCategories: visibleCategoriesForHost,
    getNewestFrameAgeMs: newestFrameAgeMs,
    onRetry: retryHosts,
  });
  initPaneRadarSite(pane0);
  radarSite = pane0.radarSite;
  initPaneCrosshair(pane0);
  initPaneTraffic(pane0);

  // Restore the tool selection from the previous session: the FAB's
  // tap-default always, plus re-arming if a tool was armed when the user
  // left. The TOOL_ICONS guard drops garbage and renamed/removed tools.
  // Must run after initPaneCrosshair (a re-armed Tähtäin shows its reticle
  // via syncCrosshairVisibility, which skips panes with no crosshair yet)
  // and before any other syncToolGroup call (which would persist the
  // defaults over the saved state before it's read).
  const savedTool = safeParseJSON('TOOL_STATE', null);
  if (savedTool && TOOL_ICONS[savedTool.last]) {
    lastTool = savedTool.last;
    if (savedTool.armed) tools.setActiveTool(savedTool.last);
  }
  syncToolGroup();

  const share = initShare({
    button: document.getElementById('shareButton'),
    getActivePanes: () => activePanes(),
    getFrameTimestamp: () => (startDate ? startDate.getTime() : Date.now()),
    getAttributions: shareAttributions,
    onFeedback: (text) => showCoachmark(text),
    onShared: (method) => track('share', { method }),
  });

  window.__tutka = {
    panes, map, framePools, tools, sharedView, share, speedDial,
  };

  // Floating compass speed dial (top-right). Fed by ownLocation's speed events
  // below; created first so the callback always has something to update.
  speedDial = initSpeedDial();

  // GEOLOCATION — own-location controller owns the source and the per-pane
  // marker fan-out (src/ownLocation.js); radar.js keeps IS_TRACKING and the
  // pane-0 position globals.
  ownLocation = initOwnLocation({
    projection: map.getView().getProjection(),
    getPanes: () => panes,
    telemetry,
    debug,
    onPositionChange: (coordinates, lonLat) => {
      ownPosition = coordinates;
      ownPosition4326 = lonLat;
      localStorage.setItem('metPosition', JSON.stringify(ownPosition));
      if (tools) tools.refresh();
    },
    onSpeedChange: (speed) => {
      if (speedDial) speedDial.update(speed);
    },
    onStatusChange: (status) => {
      if (status === 'fixed') {
        document.getElementById('gpsStatus').innerHTML = 'gps_fixed';
      } else if (status === 'denied') {
        // Permission denied: the controller has already stopped tracking and
        // hidden the marker layers; drop the persisted flag so a dead state
        // is not re-armed on every boot.
        IS_TRACKING = false;
        localStorage.setItem('IS_TRACKING', JSON.stringify(false));
        document.getElementById('gpsStatus').innerHTML = 'gps_not_fixed';
        setButtonStates();
        track('tracking-denied');
      } else {
        // 'searching' / 'stale' / 'error' — no usable fix right now.
        document.getElementById('gpsStatus').innerHTML = 'gps_not_fixed';
      }
    },
    onVesselInfo: (info) => {
      if (ownLocationMenu) ownLocationMenu.setVesselInfo(info);
    },
  });

  // Pane 0 is built long before this point, so initPaneTraffic could not have
  // attached the own-location hit-test — the controller did not exist yet.
  // Later panes get theirs there, where it does.
  pane0.ownTelemetry = ownLocation.attachPane(pane0);

  ownLocationMenu = initOwnLocationMenu({
    getSource: () => ownLocation.getSource(),
    getMmsi: () => ownLocation.getMmsi(),
    onSelectSource: (source) => ownLocation.setSource(source),
    onMmsiCommit: (value) => ownLocation.setMmsi(value),
  });

  placeSearch = initPlaceSearch({
    button: document.getElementById('placeSearchButton'),
    view: sharedView,
    onHighlight: (coord) => searchHighlight.pulseAt(coord),
  });

  // Layers
  satelliteLayer.on('change:visible', onChangeVisible);
  radarLayer.on('change:visible', onChangeVisible);
  lightningLayer.on('change:visible', onChangeVisible);
  observationLayer.on('change:visible', onChangeVisible);

  // Pane 0's own mini pill — hidden in 1-up (the global toolbar drives pane 0),
  // shown when a split layout is active.
  buildPanePill(pane0);

  map.on('click', (evt) => {
    if (tools && DRAW_TOOL_NAMES.has(tools.getActiveTool())) return;
    const hit = map.forEachFeatureAtPixel(evt.pixel, (f) => f);
    const pin = tools && tools.getPinFeature();

    // Measurement mode: route taps to the measure tool. Snap to the
    // feature's own coordinate when the tap hits a Point feature so
    // users can measure from a radar site or airfield exactly.
    if (tools && tools.isArmed()) {
      let coord = evt.coordinate;
      if (hit && hit.getGeometry && hit.getGeometry().getType() === 'Point') {
        coord = hit.getGeometry().getCoordinates();
      }
      tools.handleMeasureTap(coord);
      return;
    }

    // Pistemittaus mode: tapping our own pin toggles its card; otherwise
    // drop/move the probe pin (which opens the dBZ chart).
    if (tools && tools.isProbeArmed()) {
      if (pin && hit === pin) {
        tools.toggleCard();
        return;
      }
      displayFeatureInfo(evt.pixel);
      tools.dropOrMove(evt.coordinate);
      return;
    }

    // No tool armed: tap on our own pin (if one lingers) toggles its card;
    // otherwise just feature info for stations. Empty-map taps are inert —
    // no pin is dropped (this is the touch-annoyance fix).
    if (pin && hit === pin) {
      tools.toggleCard();
      return;
    }

    // Tap on a radar-site marker → open its drill-in card (single-site WMS
    // toggle). Coverage rings are coupled to the single-site display (drawn on
    // toggle-on, cleared on toggle-off), not to the tap. The layer-aware
    // hit-test lives in radarSite (findSiteAtPixel).
    if (radarSite) {
      const radarSiteHit = radarSite.findSiteAtPixel(evt.pixel);
      if (radarSiteHit) {
        // Drop any leftover station highlight from a previous plain-feature tap.
        if (highlight) {
          featureOverlay.getSource().removeFeature(highlight);
          highlight = null;
        }
        radarSite.openCardForFeature(radarSiteHit);
        return;
      }
    }

    // Tap on a camera marker → open the image panel. Checked before the traffic
    // markers: cameras only render from ~z8 in, so a tap that hits one is
    // deliberate and the marks are small enough to sit near an incident.
    if (pane0.cameras) {
      const cameraHit = pane0.cameras.findAtPixel(evt.pixel);
      if (cameraHit) {
        pane0.cameras.open(cameraHit);
        return;
      }
    }

    // Tap on our own marker → open its readings. Checked before the POI layers:
    // it is the one mark the user placed themselves, and it is often sitting on
    // top of whatever else is at that spot.
    if (pane0.ownTelemetry) {
      const ownHit = pane0.ownTelemetry.findAtPixel(evt.pixel);
      if (ownHit) {
        pane0.ownTelemetry.open();
        return;
      }
    }

    // Tap on an aircraft → open its card. First among the POI hit-tests: the
    // marks are small, they move, and a tap that lands on one is deliberate.
    if (pane0.gliders) {
      const planeHit = pane0.gliders.findAtPixel(evt.pixel);
      if (planeHit) {
        pane0.gliders.open(planeHit);
        return;
      }
    }

    // Tap on a METAR plot → open the report in the telemetry strip.
    if (pane0.metar) {
      const metarHit = pane0.metar.findAtPixel(evt.pixel);
      if (metarHit) {
        pane0.metar.open(metarHit);
        return;
      }
    }

    // Tap on a rescue vessel → open its telemetry strip. With the other live
    // movers: the targets are small, they move, and a tap that lands on one is
    // deliberate.
    if (pane0.rescueVessels) {
      const vesselHit = pane0.rescueVessels.findAtPixel(evt.pixel);
      if (vesselHit) {
        pane0.rescueVessels.open(vesselHit);
        return;
      }
    }

    // Tap on a moving train → open its telemetry strip. Before the station
    // check for the same reason as in initNewPane: a train at a platform
    // overlaps the station marker, and the moving target is the deliberate tap.
    if (pane0.trainLocations) {
      const trainHit = pane0.trainLocations.findAtPixel(evt.pixel);
      if (trainHit) {
        pane0.trainLocations.open(trainHit);
        return;
      }
    }

    // Tap on a railway station → open its departure board.
    if (pane0.trains) {
      const stationHit = pane0.trains.findAtPixel(evt.pixel);
      if (stationHit) {
        pane0.trains.open(stationHit);
        return;
      }
    }

    // Tap on a traffic-announcement marker → open its card. Checked after the
    // radar sites (a site tap changes what the map shows, so it keeps
    // priority) and before the generic station readout.
    if (pane0.traffic) {
      const trafficHit = pane0.traffic.findAtPixel(evt.pixel);
      if (trafficHit) {
        pane0.traffic.openFor(trafficHit);
        return;
      }
    }

    // Tap on a storm cell → open its readings in the telemetry strip. Last of
    // the POI hit-tests: a cell's target is its whole footprint, kilometres
    // across, so it would otherwise swallow taps meant for the small markers
    // that sit under the storm.
    if (pane0.stormCells) {
      const cellHit = pane0.stormCells.findAtPixel(evt.pixel);
      if (cellHit) {
        pane0.stormCells.open(cellHit);
        return;
      }
    }
    if (pane0.weatherWarnings.handleClick(evt.pixel)) return;
    displayFeatureInfo(evt.pixel);
  });

  // Clock interaction gating + metZoom persistence, wired per pane (see
  // wirePaneClockGating / onPaneMoveEnd). pane 0 is wired here; new panes get
  // wired in initNewPane.
  wirePaneClockGating(pane0);

  // Keep every pane's map sized on viewport resize and orientation flips (the
  // 2-up grid swaps cols↔rows on rotation purely in CSS; JS just re-measures).
  window.addEventListener('resize', resizeAllPanes);
  window.matchMedia('(orientation: portrait)').addEventListener('change', resizeAllPanes);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Control' && !isTextEntryTarget(event.target)) {
      document.getElementById('help').style.display = 'block';
    }
  });

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (x) => {
    // Only follow OS changes while in auto mode (no explicit user choice)
    if (IS_DARK !== null) return;
    const shown = x.matches ? 'dark' : 'light';
    setMapLayer(shown);
    track('theme-change', { pref: 'auto', shown });
  });

  if (IS_FOLLOWING) {
    setTime('last');
  } else {
    play();
  }

  if (IS_TRACKING) {
    ownLocation.setTracking(true);
  }

  // Position map
  if (metPosition.length > 1) {
    map.getView().setCenter(metPosition);
    map.getView().setZoom(metZoom);
  } else {
    map.getView().fit(transformExtent([19.24, 58.5, 31.59, 71.0], 'EPSG:4326', map.getView().getProjection()));
  }
  sync(map);
  // First word to the airspace load gate: a map that boots with the topic on
  // and is never touched fires no moveend, so the country under it would never
  // be fetched.
  airspace.setViewExtent(currentViewExtent());
};

function trackBoot({ capable, mode, error }) {
  const displayMode = window.matchMedia('(display-mode: standalone)').matches ? 'standalone'
    : window.matchMedia('(display-mode: fullscreen)').matches ? 'fullscreen'
      : window.matchMedia('(display-mode: minimal-ui)').matches ? 'minimal-ui'
        : 'browser';
  const props = {
    'display-mode': displayMode,
    'theme-pref': getThemeMode(),
    'theme-shown': getEffectiveTheme(),
    'radar-visible': VISIBLE.has('radarLayer'),
    'satellite-visible': VISIBLE.has('satelliteLayer'),
    'lightning-visible': VISIBLE.has('lightningLayer'),
    'observation-visible': VISIBLE.has('observationLayer'),
    'own-location-source': ownLocation ? ownLocation.getSource() : 'gps',
    'interp-capable': capable,
    'interp-mode': mode,
    'build-date': BUILD_DATE,
    'ol-version': OL_VERSION,
  };
  if (error) props['interp-error'] = true;
  track('app-boot', props);
}

// Listen for the appinstalled event
window.addEventListener('appinstalled', () => {
  debug('PWA was installed');
  // Track successful PWA installation
  track('pwa-installed');
});

// Map-time ETA: countdown to the next image arrival.
// Behaviour: on first detection (page load) start at the layer's full
// resolution (e.g. 5:00 for a 5-min layer). Decrement each second.
// When a new image actually lands — detected by info.time.end advancing
// — RESET the counter back to the full resolution. The first wait may
// be inaccurate (we don't know how recent the latest frame is) but
// subsequent cycles align with real arrival cadence.
const ETA_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const etaEl = document.getElementById('currentMapEta');
const etaValueEl = etaEl ? etaEl.querySelector('.eta-value') : null;

let etaSeenEnd = null;
let etaResolution = null;
let etaResetAt = Date.now();

// Attribution strings baked into the share capture: the basemap credit plus
// every active pane's visible products (same iteration as pickEtaSourceLayer).
// Before GetCapabilities resolves, layerInfo is empty and only the basemap
// credit comes out — accepted degraded case in the first seconds after boot.
function shareAttributions() {
  const parts = new Set(['Tiles © ArcGIS', 'Nimistö © Maanmittauslaitos']);
  for (const pane of activePanes()) {
    for (const name of pane.VISIBLE) {
      const olLayer = pane.layerss[name];
      const wmslayer = olLayer && olLayer.getSource().getParams().LAYERS;
      const info = wmslayer && layerInfo[wmslayer];
      if (info && info.attribution && info.attribution.Title) {
        parts.add(info.license ? `${info.attribution.Title} (${info.license})` : info.attribution.Title);
      }
    }
  }
  // POI vector layers carry no layerInfo — credit the ones that need it while on.
  // openAIP is CC BY-NC, so its share of this layer makes the credit a licence
  // condition rather than a courtesy — see the airspace line below.
  if (POI_STATE.airfields) parts.add('Lentopaikat © Fintraffic ANS / SIA / openAIP (CC BY-NC 4.0)');
  if (POI_STATE.turnpoints) parts.add('Käännöspisteet © Ilmailuliitto');
  if (POI_STATE.tuuli || POI_STATE.sateenliike) parts.add(wind.attribution);
  // Required: openAIP is CC BY-NC, so the credit is a licence condition rather
  // than a courtesy.
  if (POI_STATE.airspace) parts.add('Ilmatilat © openAIP (CC BY-NC 4.0)');
  if (POI_STATE.metar) parts.add('METAR © MET Norway (CC BY 4.0)');
  if (POI_STATE.pelastusalukset) parts.add('Pelastusalukset © Fintraffic (CC BY 4.0)');
  if (POI_STATE.gliders) parts.add('Lentokoneet © Open Glider Network');
  // Credited per part now that they can be switched independently — showing
  // only the track network should not claim to be showing Fintraffic's trains.
  if (isPoiOn('railwaystations', 'stations')) parts.add('Rautatieasemat © Fintraffic (CC BY 4.0)');
  if (isPoiOn('railwaystations', 'tracks')) parts.add('Rataverkko © Väylävirasto');
  if (isPoiOn('railwaystations', 'trains')) parts.add('Junat © Fintraffic (CC BY 4.0)');
  // Kunnat is MML Maastotietokanta (CC BY 4.0) since the switch off the
  // Tilastokeskus-derived collection, so it now needs crediting like the rest.
  if (POI_STATE.municipalities) parts.add('Kunnat © Maanmittauslaitos');
  if (POI_STATE.stormcells) parts.add('Soluntunnistus © FMI (CC BY 4.0)');
  if (POI_STATE.ukkosvaroitukset || POI_STATE.tuulivaroitukset || POI_STATE.sadevaroitukset) {
    parts.add('Varoitukset © Meteoalarm / kansalliset sääpalvelut');
  }
  return [...parts];
}

function pickEtaSourceLayer() {
  let best = null;
  // The ETA chip is shared, so pick the freshest source across every active
  // pane's visible layers (matches the shared timeline window).
  for (const pane of activePanes()) {
    for (const name of pane.VISIBLE) {
      const olLayer = pane.layerss[name];
      // timeLayerFor: in nowcast mode the mounted slot's LAYERS flips per
      // frame between the observed and forecast product; the ETA must track
      // the observed feed's cadence, not the +2 h forecast end.
      const wmslayer = olLayer && timeLayerFor(olLayer.getSource().getParams().LAYERS);
      const info = wmslayer && layerInfo[wmslayer] && layerInfo[wmslayer].time;
      if (info && info.end && info.resolution
        && Date.now() - info.end <= ETA_STALE_THRESHOLD_MS
        && (!best || info.resolution < best.resolution)) {
        best = info;
      }
    }
  }
  return best;
}

function updateMapEta() {
  if (!etaEl || !etaValueEl) return;
  const info = pickEtaSourceLayer();
  if (!info) {
    etaEl.hidden = true;
    etaSeenEnd = null;
    etaResolution = null;
    return;
  }

  // Reset whenever we first see a layer or its newest frame advances.
  if (info.end !== etaSeenEnd || info.resolution !== etaResolution) {
    etaSeenEnd = info.end;
    etaResolution = info.resolution;
    etaResetAt = Date.now();
  }

  const remaining = etaResolution - (Date.now() - etaResetAt);
  etaEl.hidden = false;
  if (remaining > 0) {
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    etaValueEl.textContent = `${mins}:${String(secs).padStart(2, '0')}`;
  } else {
    etaValueEl.textContent = 'odotetaan';
  }
}

setInterval(updateMapEta, 1000);
updateMapEta();

// Time chip — small clock in the bottom-left corner. Click to toggle local
// time vs UTC; the choice persists across sessions. In app-fullscreen mode the
// same pill instead shows the displayed frame's DATA time (see renderTimeChip).
let timeIsUtc = safeParseJSON('timeIsUtc', false);
const timeChipEl = document.getElementById('timeChip');
const tcTime = timeChipEl && timeChipEl.querySelector('.tc-time');
const tcDate = timeChipEl && timeChipEl.querySelector('.tc-date');

function renderTimeChip() {
  if (!timeChipEl) return;
  // Fullscreen shows the displayed frame's data time (mapTime); otherwise the
  // wall-clock "now". Falls back to now if no frame has loaded yet.
  const base = (appFullscreen && mapTime) ? dayjs(mapTime) : dayjs();
  const d = timeIsUtc ? base.utc() : base;
  tcTime.textContent = d.format('HH:mm');
  tcDate.textContent = timeIsUtc
    ? `${d.format('dd D.M.')} UTC`
    : d.format('dd D.M.');
  timeChipEl.classList.toggle('utc-mode', timeIsUtc);
}

if (timeChipEl) {
  timeChipEl.addEventListener('click', (e) => {
    e.stopPropagation();
    timeIsUtc = !timeIsUtc;
    localStorage.setItem('timeIsUtc', JSON.stringify(timeIsUtc));
    renderTimeChip();
    track('time-chip-toggle', { utc: timeIsUtc });
  });

  renderTimeChip();
  setInterval(renderTimeChip, 1000);
}

// App-level fullscreen ("Koko ruutu") — hides all chrome except the data-time
// chip (bottom-left) and the exit FAB (bottom-right). Transient view mode, not
// persisted across reloads.
function setAppFullscreen(on) {
  appFullscreen = on;
  document.body.classList.toggle('app-fullscreen', on);
  if (on) {
    closeOverflowMenu();
    closeToolFlyout();
  }
  renderTimeChip(); // swap now ⇄ data time immediately
  track(on ? 'fullscreen-on' : 'fullscreen-off');
}

const fullscreenEnterButton = document.getElementById('fullscreenEnterButton');
const fullscreenExitButton = document.getElementById('fullscreenExitButton');
if (fullscreenEnterButton) {
  fullscreenEnterButton.addEventListener('click', () => setAppFullscreen(true));
}
if (fullscreenExitButton) {
  fullscreenExitButton.addEventListener('click', () => setAppFullscreen(false));
}

main();
